import React, { useState, useEffect, useRef } from 'react';
import { X, Info, Zap, GitBranch, Send, Mail, Sparkles, Tag, Users, CheckSquare, AlertTriangle, Target } from 'lucide-react';
import api from '../../utils/api';
import { detectBranchConflicts, getWarningSeverityClass } from '../../utils/conditionConflictDetector';

// Variable Picker Component with Tag Icon
const VariablePicker = ({
  onInsert,
  includeAI = false,
  customFields = [],
  clientCustomFields = [],
  projectCustomFields = [],
  webhookVariables = [],
  portalFields = []
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Build portal submission items including dynamic fields
  const portalSubmissionItems = [
    { label: 'Request Type', value: '{{portal.request_type}}' },
    { label: 'Submission Title', value: '{{portal.title}}' },
    { label: 'Submission Description', value: '{{portal.description}}' },
    { label: 'Submission Priority', value: '{{portal.priority}}' },
    { label: 'Attachments Count', value: '{{portal.attachments_count}}' },
    { label: 'Contact Name', value: '{{contact.name}}' },
    { label: 'Contact Email', value: '{{contact.email}}' },
    // Add dynamic portal fields
    ...portalFields.map(field => ({
      label: `Form: ${field.label}`,
      value: field.variable
    }))
  ];

  const variables = [
    { group: 'Task', items: [
      { label: 'Task Name', value: '{{task.name}}' },
      { label: 'Task Stage', value: '{{task.stage}}' },
      { label: 'Task Description', value: '{{task.description}}' },
      { label: 'Task Category', value: '{{task.category}}' },
      { label: 'Task Priority', value: '{{task.priority}}' },
      { label: 'Task Due Date', value: '{{task.due_date}}' },
    ]},
    { group: 'Project', items: [
      { label: 'Project Name', value: '{{project.name}}' },
      { label: 'Project Description', value: '{{project.description}}' },
      { label: 'Project Status', value: '{{project.status}}' },
      { label: 'Project Category', value: '{{project.category}}' },
      { label: 'Project Due Date', value: '{{project.due_date}}' },
      { label: 'Project ID', value: '{{project.id}}' },
    ]},
    { group: 'Client', items: [
      { label: 'Client Name', value: '{{client.name}}' },
      { label: 'Client Email', value: '{{client.email}}' },
      { label: 'Client Phone', value: '{{client.phone}}' },
      { label: 'Client ID', value: '{{client.id}}' },
    ]},
    { group: 'Assignee', items: [
      { label: 'Assignee Name', value: '{{assignee.name}}' },
      { label: 'Assignee Email', value: '{{assignee.email}}' },
    ]},
    { group: 'Organization', items: [
      { label: 'Portal Link', value: '{{portal.link}}' },
      { label: 'Organization Name', value: '{{organization.name}}' },
    ]},
    { group: 'Portal Submission', items: portalSubmissionItems },
    // Webhook Payload variables - common patterns
    { group: 'Webhook Payload', items: [
      { label: 'Full Payload', value: '{{webhook_payload}}' },
      { label: 'Webhook Client Name', value: '{{webhook_payload.client.name}}' },
      { label: 'Webhook Client Email', value: '{{webhook_payload.client.email}}' },
      { label: 'Webhook Task Name', value: '{{webhook_payload.task.name}}' },
      { label: 'Webhook Task Description', value: '{{webhook_payload.task.description}}' },
      { label: 'Webhook Project Name', value: '{{webhook_payload.project.name}}' },
      { label: 'Webhook Source', value: '{{webhook_payload.source}}' },
      // Add any webhook variables passed from the trigger config
      ...webhookVariables.map(v => ({ label: v.path.replace('webhook_payload.', ''), value: `{{${v.path}}}` }))
    ]},
    // Workflow context variables
    { group: 'Workflow', items: [
      { label: 'Trigger Type', value: '{{trigger_type}}' },
      { label: 'Workflow Name', value: '{{workflow_name}}' },
      { label: 'Current Date', value: '{{current_date}}' },
      { label: 'Current Time', value: '{{current_time}}' },
    ]},
    // GPT Results - supports multiple GPT actions in sequence
    { group: 'GPT Results', items: [
      { label: 'GPT Result (Latest)', value: '{{gpt_result}}' },
      { label: 'GPT Result #1', value: '{{gpt_result_1}}' },
      { label: 'GPT Result #2', value: '{{gpt_result_2}}' },
      { label: 'GPT Result #3', value: '{{gpt_result_3}}' },
      { label: 'GPT Result #4', value: '{{gpt_result_4}}' },
      { label: 'GPT Result #5', value: '{{gpt_result_5}}' },
    ]},
  ];

  // Add custom/advanced fields by entity
  const taskCustomItems = (customFields || []).map(field => ({
    label: field.label || field.name,
    value: `{{custom_field.${field.name}}}`
  }));

  const clientCustomItems = (clientCustomFields || []).map(field => {
    const varName = field.variable_name || field.name;
    return {
      label: field.label || field.name,
      value: `{{client.${varName}}}`
    };
  });

  const projectCustomItems = (projectCustomFields || []).map(field => {
    const varName = field.variable_name || field.name;
    return {
      label: field.label || field.name,
      value: `{{project.${varName}}}`
    };
  });

  if (taskCustomItems.length > 0) variables.push({ group: 'Task Custom Fields', items: taskCustomItems });
  if (clientCustomItems.length > 0) variables.push({ group: 'Client Custom Fields', items: clientCustomItems });
  if (projectCustomItems.length > 0) variables.push({ group: 'Project Custom Fields', items: projectCustomItems });

  if (includeAI) {
    variables.push({ group: 'AI Generated', items: [
      { label: 'AI Subject', value: '{{ai_content.subject}}' },
      { label: 'AI Body', value: '{{ai_content.body}}' },
    ]});
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
        title="Insert Variable"
      >
        <Tag className="w-4 h-4" />
      </button>
      
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={() => setIsOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900">Insert Variable</h3>
              <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-80">
              {variables.map((group, idx) => (
                <div key={idx} className="border-b border-gray-100 last:border-b-0">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                    {group.group}
                  </div>
                  <div className="py-1">
                    {group.items.map((item, itemIdx) => (
                      <button
                        key={itemIdx}
                        type="button"
                        onClick={() => {
                          onInsert(item.value);
                          setIsOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors flex justify-between items-center group"
                      >
                        <span className="text-sm text-gray-700 group-hover:text-purple-700">{item.label}</span>
                        <code className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-mono">{item.value}</code>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to build webhook payload preview
const buildWebhookPayloadPreview = (actionConfig) => {
  const selectedFields = actionConfig?.selectedFields || [];
  const selectedCustomFields = actionConfig?.selectedCustomFields || [];
  const includeTimestamp = actionConfig?.includeTimestamp !== false;
  const includeTriggerType = actionConfig?.includeTriggerType !== false;
  const includeWorkflowInfo = actionConfig?.includeWorkflowInfo || false;

  const payload = {};

  // Add metadata
  if (includeTimestamp) {
    payload.timestamp = "2024-01-15T10:30:00Z";
  }
  if (includeTriggerType) {
    payload.event = "task_updated";
  }
  if (includeWorkflowInfo) {
    payload.workflow = {
      id: "workflow_123",
      name: "Task Status Workflow"
    };
  }

  // Add selected standard fields
  const fieldGroups = {
    task: {},
    client: {},
    project: {},
    portal: {}
  };

  selectedFields.forEach(field => {
    const parts = field.split('.');
    const group = parts[0]; // task, client, or project
    
    if (fieldGroups[group]) {
      // Handle nested custom_fields
      if (parts[1] === 'custom_fields' && parts.length === 3) {
        const customFieldName = parts[2];
        if (!fieldGroups[group].custom_fields) {
          fieldGroups[group].custom_fields = {};
        }
        fieldGroups[group].custom_fields[customFieldName] = `Sample ${customFieldName} value`;
      } else {
        // Standard field
        const fieldName = parts.slice(1).join('.');
        
        // Sample values for preview
        const sampleValues = {
          'id': `${group}_123`,
          'name': `Sample ${group.charAt(0).toUpperCase() + group.slice(1)}`,
          'description': `${group} description here`,
          'status': 'In Progress',
          'priority': 'high',
          'category': 'Development',
          'due_date': '2024-01-20',
          'start_date': '2024-01-15',
          'assignees': ['user1@example.com', 'user2@example.com'],
          'tags': ['urgent', 'client-facing'],
          'estimated_effort': '4 hours',
          'percent_complete': 75,
          'is_milestone': false,
          'show_in_portal': true,
          'email': 'contact@example.com',
          'phone': '+1-555-0123',
          'website': 'https://example.com',
          'address': '123 Business St',
          'industry': 'Technology',
          'company_size': '50-100',
          'source': 'Referral',
          'request_type': 'Feature Request',
          'request_subtype': 'event',
          'attachments_count': 2,
          'form_version': 1,
          'form_data': { post_type: 'event', event_date: '2026-03-10' },
          'form_data_flat': { post_type: 'event', event_date: '2026-03-10' },
          'request_in_scope': true,
          'matching_scope': 'Website Support'
        };
        
        fieldGroups[group][fieldName] = sampleValues[fieldName] || `{{${field}}}`;
      }
    }
  });

  // Add non-empty field groups to payload
  Object.keys(fieldGroups).forEach(group => {
    if (Object.keys(fieldGroups[group]).length > 0) {
      payload[group] = fieldGroups[group];
    }
  });

  // Add custom fields
  if (selectedCustomFields.length > 0) {
    payload.custom_fields = {};
    selectedCustomFields.forEach(fieldId => {
      payload.custom_fields[`field_${fieldId}`] = `{{custom_field.${fieldId}}}`;
    });
  }

  return payload;
};

const NodeConfigModal = ({ node, workflowId, allNodes = [], webhookPayloadVariables = [], onWebhookVariablesChange, onClose, onSave, onSaveWorkflow }) => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [taskCustomFields, setTaskCustomFields] = useState([]);
  const [projectCustomFields, setProjectCustomFields] = useState([]);
  const [clientCustomFields, setClientCustomFields] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [portalFields, setPortalFields] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [generatingWebhook, setGeneratingWebhook] = useState(false);
  const [organizationName, setOrganizationName] = useState('Company');
  const [currentWorkflowId, setCurrentWorkflowId] = useState(workflowId);
  const [applyStatus, setApplyStatus] = useState(null); // 'saving', 'saved', null
  // Use passed-in webhook variables, fall back to local state
  const [localWebhookVariables, setLocalWebhookVariables] = useState([]);
  const webhookVariables = webhookPayloadVariables.length > 0 ? webhookPayloadVariables : localWebhookVariables;
  const setWebhookVariables = onWebhookVariablesChange || setLocalWebhookVariables;
  
  // Webhook-related state for inbound webhook trigger
  const [webhookRequests, setWebhookRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [extractedVariables, setExtractedVariables] = useState([]);
  const [requestTypeOptions, setRequestTypeOptions] = useState([]);
  
  // Refs for textarea cursor position
  const instructionsRef = useRef(null);
  const emailSubjectRef = useRef(null);
  const emailBodyRef = useRef(null);

  const fetchOrganizationName = async () => {
    try {
      // First try to get from custom values (company_name variable)
      const customValuesResponse = await api.get('/custom-values?category=company');
      if (customValuesResponse.data) {
        const companyNameValue = customValuesResponse.data.find(v => v.variable_name === 'company_name');
        if (companyNameValue?.value) {
          setOrganizationName(companyNameValue.value);
          return;
        }
      }
      
      // Fall back to team name from current organization
      const orgResponse = await api.get('/user/current-organization');
      if (orgResponse.data?.name) {
        setOrganizationName(orgResponse.data.name);
      }
    } catch (err) {
      console.error('Error fetching organization name:', err);
      // Keep default 'Company'
    }
  };

  const fetchCustomFields = async () => {
    try {
      // Fetch custom fields for all entity types
      const [taskResponse, projectResponse, clientResponse] = await Promise.all([
        api.get('/custom-field-definitions?entity_type=task'),
        api.get('/custom-field-definitions?entity_type=project'),
        api.get('/custom-field-definitions?entity_type=client')
      ]);
      
      // API returns array directly, not wrapped in .definitions
      if (taskResponse.data) {
        const taskFields = Array.isArray(taskResponse.data) ? taskResponse.data : [];
        setTaskCustomFields(taskFields);
        // Keep legacy state for backward compatibility
        setCustomFields(taskFields);
        setCustomFieldDefinitions(taskFields);
      }
      
      if (projectResponse.data) {
        const projectFields = Array.isArray(projectResponse.data) ? projectResponse.data : [];
        setProjectCustomFields(projectFields);
      }
      
      if (clientResponse.data) {
        const clientFields = Array.isArray(clientResponse.data) ? clientResponse.data : [];
        setClientCustomFields(clientFields);
      }
    } catch (err) {
      console.error('Error fetching custom fields:', err);
    }
  };

  const fetchTeamUsers = async () => {
    try {
      const response = await api.get('/team/members');
      if (response.data) {
        setTeamUsers(response.data);
      }
    } catch (err) {
      console.error('Error fetching team users:', err);
    }
  };

  const fetchTaskTemplates = async () => {
    try {
      const response = await api.get('/templates/tasks');
      if (response.data) {
        setTaskTemplates(response.data);
      }
    } catch (err) {
      console.error('Error fetching task templates:', err);
    }
  };

  const fetchProjectTemplates = async () => {
    try {
      const response = await api.get('/templates/projects');
      if (response.data) {
        setProjectTemplates(response.data);
      }
    } catch (err) {
      console.error('Error fetching project templates:', err);
    }
  };

  const fetchProjectCategories = async () => {
    try {
      const response = await api.get('/project-categories');
      if (response.data && Array.isArray(response.data)) {
        setProjectCategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching project categories:', err);
      // Fall back to defaults
      setProjectCategories(['General', 'Website', 'Marketing', 'Development', 'Consulting', 'Support']);
    }
  };

  const fetchRequestTypes = async () => {
    try {
      const response = await api.get('/portal/settings');
      if (response.data?.request_types) {
        setPortalFields(response.data.request_types.map(type => ({ label: type, value: type })));
      }
    } catch (err) {
      console.error('Error fetching request types:', err);
    }
  };

  // Fetch portal fields (and initialize request types)
  const fetchPortalFields = async () => {
    try {
      const response = await api.get('/workflow-automations/portal-fields');
      if (response.data?.fields) {
        setPortalFields(response.data.fields);
      }
      // Fetch request types as well, and populate them
      fetchRequestTypes();
    } catch (err) {
      console.error('Error fetching portal fields:', err);
    }
  };

  const fetchTaskCategories = async () => {
    try {
      const response = await api.get('/task-categories');
      if (response.data && Array.isArray(response.data)) {
        setTaskCategories(response.data);
      }
    } catch (err) {
      console.error('Error fetching task categories:', err);
    }
  };

  const fetchRequestTypeOptions = async () => {
    try {
      const response = await api.get('/portal/settings');
      if (response.data?.request_types) {
        setRequestTypeOptions(response.data.request_types.map(type => ({ label: type, value: type })));
      }
    } catch (err) {
      console.error('Error fetching request types for conditions:', err);
    }
  };

  // Load existing webhook info from the workflow if available
  const loadExistingWebhookInfo = async () => {
    const targetId = currentWorkflowId || workflowId;
    if (!targetId) return;
    
    // Check if this is an inbound webhook trigger
    const triggerType = node?.data?.triggerType;
    if (triggerType !== 'inbound_webhook') return;
    
    try {
      const response = await api.get(`/workflow-automations/${targetId}`);
      const workflow = response.data;
      
      // If webhook URL exists, populate the webhook info
      if (workflow.webhook_url && workflow.webhook_secret) {
        setWebhookInfo({
          webhook_url: workflow.webhook_url,
          webhook_secret: workflow.webhook_secret
        });
      }
    } catch (err) {
      console.log('Could not load existing webhook info:', err);
    }
  };

  // Load webhook variables from the workflow's inbound webhook trigger config
  const loadWebhookVariables = async () => {
    // First, try to get from local allNodes (for unsaved changes / real-time updates)
    if (allNodes && allNodes.length > 0) {
      const triggerNode = allNodes.find(n => n.type === 'trigger');
      if (triggerNode?.data?.triggerType === 'inbound_webhook') {
        const samplePayload = triggerNode.data?.triggerConfig?.samplePayload;
        if (samplePayload) {
          try {
            const parsed = JSON.parse(samplePayload);
            const vars = extractWebhookVariables(parsed, 'webhook_payload');
            setWebhookVariables(vars);
            return; // Found variables from local nodes, no need to fetch from API
          } catch (e) {
            // Invalid JSON, continue to API fallback
          }
        }
      }
    }
    
    // Fallback: fetch from API if workflowId exists
    if (!workflowId) return;
    
    try {
      const response = await api.get(`/workflow-automations/${workflowId}`);
      
      const workflow = response.data;
      const nodes = workflow.nodes || [];
      
      // Find the trigger node
      const triggerNode = nodes.find(n => n.type === 'trigger');
      if (triggerNode?.data?.triggerType === 'inbound_webhook') {
        const samplePayload = triggerNode.data?.triggerConfig?.samplePayload;
        if (samplePayload) {
          try {
            const parsed = JSON.parse(samplePayload);
            const vars = extractWebhookVariables(parsed, 'webhook_payload');
            setWebhookVariables(vars);
          } catch (e) {
            // Invalid JSON
          }
        }
      }
    } catch (err) {
      console.log('Could not load webhook variables:', err);
    }
  };

  // Extract variables from a nested object
  const extractWebhookVariables = (obj, prefix) => {
    const vars = [];
    const extract = (o, path) => {
      for (const [key, value] of Object.entries(o)) {
        const fullPath = `${path}.${key}`;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          extract(value, fullPath);
        } else {
          vars.push({ path: fullPath, value });
        }
      }
    };
    extract(obj, prefix);
    return vars;
  };

  useEffect(() => {
    // Load existing config
    if (node?.data) {
      setConfig({
        ...node.data,
        triggerConfig: node.data.triggerConfig || {},
        conditionConfig: node.data.conditionConfig || {},
        actionConfig: node.data.actionConfig || {}
      });
    }

    // Always fetch custom fields for variable picker
    fetchCustomFields();
    fetchTeamUsers();
    fetchTaskCategories();
    fetchTaskTemplates();
    fetchProjectTemplates();
    fetchProjectCategories();
    fetchPortalFields();
    fetchOrganizationName();
    fetchRequestTypeOptions(); // <-- Add this line
    
    // Load webhook variables if this workflow has a webhook trigger
    loadWebhookVariables();
    
    // Load existing webhook info if this is an inbound_webhook trigger
    loadExistingWebhookInfo();
  }, [node, allNodes]);

  const handleSave = () => {
    const normalizedConfig = { ...config };
    if (normalizedConfig.actionType === 'update_assignees') {
      const currentActionConfig = normalizedConfig.actionConfig || {};
      normalizedConfig.actionConfig = {
        assignee_action: currentActionConfig.assignee_action || 'add',
        assignee_source: currentActionConfig.assignee_source || 'specific_users',
        ...currentActionConfig
      };
    }

    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        ...normalizedConfig
      }
    };
    onSave(updatedNode);
    
    // Show saved confirmation
    setApplyStatus('saved');
    setTimeout(() => setApplyStatus(null), 2000);
  };

  // Helper to insert variable at cursor position
  const insertAtCursor = (textareaRef, fieldPath, value) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = fieldPath.split('.').reduce((obj, key) => obj?.[key], config) || '';
    const newValue = currentValue.substring(0, start) + value + currentValue.substring(end);
    
    // Update config
    const keys = fieldPath.split('.');
    const lastKey = keys.pop();
    const configPath = keys.reduce((obj, key) => obj[key] = obj[key] || {}, config);
    configPath[lastKey] = newValue;
    
    setConfig({ ...config });
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + value.length, start + value.length);
    }, 0);
  };

  // Webhook-related functions for inbound webhook trigger
  const fetchWebhookRequests = async () => {
    const targetId = currentWorkflowId || workflowId;
    if (!targetId) return;
    
    setLoadingRequests(true);
    try {
      const response = await api.get(`/workflow-automations/${targetId}/webhook-requests?limit=10`);
      setWebhookRequests(response.data.requests || []);
    } catch (err) {
      console.error('Error fetching webhook requests:', err);
    }
    setLoadingRequests(false);
  };

  // Extract variables from payload
  const extractVariablesFromPayload = (payload, prefix = 'webhook_payload') => {
    const vars = [];
    const extract = (obj, path) => {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = `${path}.${key}`;
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          extract(value, fullPath);
        } else {
          vars.push({ path: fullPath, value: Array.isArray(value) ? JSON.stringify(value) : value });
        }
      }
    };
    try {
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
      if (parsed && typeof parsed === 'object') {
        extract(parsed, prefix);
      }
    } catch (e) {
      // Invalid JSON
    }
    return vars;
  };

  // Handle request selection
  const handleRequestSelect = (requestId) => {
    setSelectedRequestId(requestId);
    const request = webhookRequests.find(r => r.id === requestId);
    if (request) {
      setSelectedPayload(request.payload);
      const vars = extractVariablesFromPayload(request.payload);
      setExtractedVariables(vars);
      // Update parent state so action nodes see the variables
      if (onWebhookVariablesChange) {
        onWebhookVariablesChange(vars);
      }
    } else {
      setSelectedPayload(null);
      setExtractedVariables([]);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const generateWebhookUrl = async () => {
    let targetWorkflowId = currentWorkflowId;
    
    // If workflow doesn't exist yet, auto-save it first
    if (!targetWorkflowId && onSaveWorkflow) {
      setGeneratingWebhook(true);
      try {
        // Save the current node config first
        handleSave();
        
        // Call parent to save workflow and get the new ID
        const newWorkflowId = await onSaveWorkflow();
        if (newWorkflowId) {
          targetWorkflowId = newWorkflowId;
          setCurrentWorkflowId(newWorkflowId);
        } else {
          alert('Please provide a workflow name and save the workflow first');
          setGeneratingWebhook(false);
          return;
        }
      } catch (err) {
        console.error('Error auto-saving workflow:', err);
        alert('Failed to save workflow. Please save manually first.');
        setGeneratingWebhook(false);
        return;
      }
    }
    
    if (!targetWorkflowId) {
      alert('Please save the workflow first to generate a webhook URL');
      return;
    }
    
    setGeneratingWebhook(true);
    try {
      const response = await api.post(`/workflow-automations/${targetWorkflowId}/generate-webhook`, {});
      setWebhookInfo(response.data);
    } catch (err) {
      console.error('Error generating webhook:', err);
      alert('Failed to generate webhook URL');
    }
    setGeneratingWebhook(false);
  };

  // Add useEffect to fetch webhook requests when webhook info changes
  useEffect(() => {
    if (webhookInfo) {
      fetchWebhookRequests();
    }
  }, [webhookInfo, currentWorkflowId, workflowId]);

  const renderTriggerConfig = () => {
    const triggerType = config.triggerType || node?.data?.triggerType;

    if (triggerType === 'task_stage_changed' || triggerType === 'task_status_changed') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Stage <span className="text-gray-500">(use * for any)</span>
            </label>
            <input
              type="text"
              value={config.triggerConfig?.from_status || '*'}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, from_status: e.target.value }
              })}
              placeholder="Enter stage name or use * for any"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the exact stage name (e.g., "In Progress", "To Do") or "*" for any stage
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Stage <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.triggerConfig?.to_status || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, to_status: e.target.value }
              })}
              placeholder="Enter target stage name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the exact stage name that will trigger this workflow
            </p>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow will trigger when a task moves from "{config.triggerConfig?.from_status || 'any'}" to "{config.triggerConfig?.to_status || '...'}"
          </div>
          
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Tip:</strong> Status matching is case-sensitive and matches by exact name. You can also match by status_id in the backend.
          </div>
        </div>
      );
    }

    // Task Completed trigger
    if (triggerType === 'task_completed') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when a task is marked as completed
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Project (Optional)
            </label>
            <input
              type="text"
              value={config.triggerConfig?.project_filter || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, project_filter: e.target.value }
              })}
              placeholder="Leave blank for all projects"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      );
    }

    // Task Assigned trigger
    if (triggerType === 'task_assigned') {
      const selectedAssignees = config.triggerConfig?.assignee_ids || [];
      
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when someone is assigned to a task
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Assignee (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Select specific team members to filter, or leave empty to trigger for any assignee
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2">
              {teamUsers.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Loading team members...</p>
              ) : (
                teamUsers.map(user => (
                  <label key={user.user_id || user.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAssignees.includes(user.user_id || user.id)}
                      onChange={(e) => {
                        const userId = user.user_id || user.id;
                        let newIds;
                        if (e.target.checked) {
                          newIds = [...selectedAssignees, userId];
                        } else {
                          newIds = selectedAssignees.filter(id => id !== userId);
                        }
                        setConfig({
                          ...config,
                          triggerConfig: { ...config.triggerConfig, assignee_ids: newIds }
                        });
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-700">
                          {(user.user?.name || user.name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-900">{user.user?.name || user.name}</span>
                        <span className="text-xs text-gray-500 ml-2">{user.user?.email || user.email}</span>
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
            {selectedAssignees.length > 0 && (
              <p className="text-xs text-purple-600 mt-2">
                Filtering for {selectedAssignees.length} selected user(s)
              </p>
            )}
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Tip:</strong> Leave all checkboxes unchecked to trigger this workflow when <em>any</em> team member is assigned to a task.
          </div>
        </div>
      );
    }

    // Portal Access Granted trigger
    if (triggerType === 'portal_access_granted') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when portal access is granted to a contact
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Available Variables:</strong> When this trigger fires, you can use variables like:
            <ul className="mt-2 ml-4 list-disc">
              <li><code className="bg-blue-100 px-1 rounded">{'{{contact.name}}'}</code> - Contact&apos;s name</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{contact.email}}'}</code> - Contact&apos;s email</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{client.name}}'}</code> - Client/Company name</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{portal.link}}'}</code> - Portal access link</li>
            </ul>
          </div>
        </div>
      );
    }

    // Client Created trigger
    if (triggerType === 'client_created') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when a new client is created (from any source: UI, webhook, or import)
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Available Variables:</strong> When this trigger fires, you can use:
            <ul className="mt-2 ml-4 list-disc">
              <li><code className="bg-blue-100 px-1 rounded">{'{{client.id}}'}</code> - Client ID</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{client.name}}'}</code> - Client/Company name</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{client.email}}'}</code> - Client email</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{client.type}}'}</code> - Client type (business/individual)</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{contact.name}}'}</code> - Primary contact name</li>
              <li><code className="bg-blue-100 px-1 rounded">{'{{contact.email}}'}</code> - Primary contact email</li>
            </ul>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Client Type (Optional)
            </label>
            <select
              value={config.triggerConfig?.client_type || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, client_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All client types</option>
              <option value="business">Business only</option>
              <option value="individual">Individual only</option>
            </select>
          </div>
        </div>
      );
    }

    // Priority Changed trigger
    if (triggerType === 'priority_changed') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when a task's priority changes
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Priority (Optional)
            </label>
            <select
              value={config.triggerConfig?.from_priority || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, from_priority: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Any priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Priority (Optional)
            </label>
            <select
              value={config.triggerConfig?.to_priority || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, to_priority: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Any priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
      );
    }

    // Stage Changed trigger
    if (triggerType === 'stage_changed') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when a task moves to a different project stage
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Stage Name (Optional)
            </label>
            <input
              type="text"
              value={config.triggerConfig?.to_stage || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, to_stage: e.target.value }
              })}
              placeholder="e.g., In Review (leave blank for any)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      );
    }

    // Task Due Soon trigger
    if (triggerType === 'task_due_soon') {
      return (
        <div className="space-y-4">
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers for tasks approaching their due date (runs daily)
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Days Before Due <span className="text-red-500">*</span>
            </label>
            <select
              value={config.triggerConfig?.days_before || '1'}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, days_before: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="1">1 day before</option>
              <option value="2">2 days before</option>
              <option value="3">3 days before</option>
              <option value="5">5 days before</option>
              <option value="7">7 days before</option>
            </select>
          </div>
        </div>
      );
    }

    // Comment Added trigger
    if (triggerType === 'comment_added') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when a comment is added to a task
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Commenter Email (Optional)
            </label>
            <input
              type="text"
              value={config.triggerConfig?.commenter_filter || ''}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, commenter_filter: e.target.value }
              })}
              placeholder="e.g., client@example.com (leave blank for any)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="mentions_only"
              checked={config.triggerConfig?.mentions_only || false}
              onChange={(e) => setConfig({
                ...config,
                triggerConfig: { ...config.triggerConfig, mentions_only: e.target.checked }
              })}
              className="rounded border-gray-300"
            />
            <label htmlFor="mentions_only" className="text-sm text-gray-700">
              Only trigger when comment contains @mentions
            </label>
          </div>
        </div>
      );
    }

    if (triggerType === 'client_portal_submission') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow will trigger whenever a client submits a task via the portal
          </div>
        </div>
      );
    }

    if (triggerType === 'inbound_webhook') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            This workflow triggers when external systems POST JSON data to the webhook URL. Any JSON structure is supported.
          </div>

          {/* Webhook URL Generation */}
          {(currentWorkflowId || workflowId || onSaveWorkflow) ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={generateWebhookUrl}
                disabled={generatingWebhook}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {generatingWebhook ? 'Generating...' : (webhookInfo ? 'Regenerate Webhook URL' : 'Generate Webhook URL')}
              </button>

              {webhookInfo && (
                <div className="bg-gray-50 p-4 rounded-lg border space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL (POST/GET/PUT)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={webhookInfo.webhook_url}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(webhookInfo.webhook_url)}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Secret (x-webhook-secret header)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={webhookInfo.webhook_secret}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(webhookInfo.webhook_secret)}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-yellow-50 p-3 rounded-md text-sm text-yellow-800">
              Save this workflow first to generate a webhook URL.
            </div>
          )}

          {/* Mapping Reference Section */}
          {webhookInfo && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Mapping Reference</h4>
                <button
                  type="button"
                  onClick={fetchWebhookRequests}
                  disabled={loadingRequests}
                  className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  {loadingRequests ? 'Loading...' : '↻ Refresh'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Send a test webhook from your external platform, then select it here to map the payload fields.
              </p>
              
              {/* Webhook Request Dropdown */}
              <div className="mb-4">
                <select
                  value={selectedRequestId}
                  onChange={(e) => handleRequestSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a webhook request...</option>
                  {webhookRequests.map((req) => (
                    <option key={req.id} value={req.id}>
                      {req.status === 'completed' ? '✓' : req.status === 'failed' ? '✗' : '○'} ({formatTimestamp(req.timestamp)}) Request {req.id.substring(0, 8)}...
                    </option>
                  ))}
                </select>
                {webhookRequests.length === 0 && !loadingRequests && (
                  <p className="text-xs text-gray-400 mt-1">No webhook requests received yet. Send a test from your external platform.</p>
                )}
              </div>

              {/* Payload Display and Variables */}
              {selectedPayload && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* JSON Payload Viewer */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Received Payload</label>
                    <div className="border border-gray-300 rounded-md bg-gray-900 p-3 h-64 overflow-auto">
                      <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                        {JSON.stringify(selectedPayload, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {/* Extracted Variables */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Available Variables ({extractedVariables.length})
                    </label>
                    <div className="border border-gray-300 rounded-md bg-gray-50 p-2 h-64 overflow-y-auto">
                      {extractedVariables.length > 0 ? (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-1 px-2 font-medium text-gray-600">Variable</th>
                              <th className="text-left py-1 px-2 font-medium text-gray-600">Sample Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {extractedVariables.map((v, i) => (
                              <tr key={i} className="border-b border-gray-200 hover:bg-white cursor-pointer" 
                                  onClick={() => navigator.clipboard.writeText(`{{${v.path}}}`)}>
                                <td className="py-1 px-2 font-mono text-purple-600">{`{{${v.path}}}`}</td>
                                <td className="py-1 px-2 text-gray-600 truncate max-w-32" title={String(v.value)}>
                                  {String(v.value).substring(0, 30)}{String(v.value).length > 30 ? '...' : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-gray-400 text-center py-4">Select a request to see variables</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Click a variable to copy it</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage Tips */}
          <div className="bg-blue-50 p-3 rounded-md text-xs text-blue-800">
            <strong>💡 Tips:</strong>
            <ul className="mt-1 ml-4 list-disc space-y-1">
              <li>Use <code className="bg-blue-100 px-1 rounded">{'{{webhook_payload.field}}'}</code> in any action field</li>
              <li>Nested data: <code className="bg-blue-100 px-1 rounded">{'{{webhook_payload.client.email}}'}</code></li>
              <li>If <code>client.email</code> is provided, we'll auto-match to existing clients</li>
              <li>Send a test webhook from your external platform to see the exact payload structure</li>
            </ul>
          </div>
        </div>
      );
    }

    return (
      <div className="text-gray-600 text-sm">
        No additional configuration needed for this trigger type.
      </div>
    );
  };

  const renderConditionConfig = () => {
    const conditionType = config.conditionType || node?.data?.conditionType;

    if (conditionType === 'status_equals') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status Equals <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.conditionConfig?.status || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, status: e.target.value }
              })}
              placeholder="Enter status name to match"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the exact status name (e.g., "Completed", "Blocked")
            </p>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            This condition checks if the task's current status matches the entered value
          </div>
        </div>
      );
    }

    if (conditionType === 'category_equals') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={config.conditionConfig?.category || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, category: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            >
              <option value="">Select a category...</option>
              {taskCategories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
            {taskCategories.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">Loading categories...</p>
            )}
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Checks if the task's category matches the selected value
          </div>
        </div>
      );
    }

    if (conditionType === 'portal_enabled') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Portal Enabled Status <span className="text-red-500">*</span>
            </label>
            <select
              value={config.conditionConfig?.portal_enabled !== undefined ? config.conditionConfig.portal_enabled.toString() : 'true'}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, portal_enabled: e.target.value === 'true' }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="true">Enabled (True)</option>
              <option value="false">Disabled (False)</option>
            </select>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Checks if the task is enabled/disabled for the client portal
          </div>
        </div>
      );
    }

    if (conditionType === 'priority_equals') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority Level <span className="text-red-500">*</span>
            </label>
            <select
              value={config.conditionConfig?.priority || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, priority: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select Priority...</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Checks if the task priority matches the selected level
          </div>
        </div>
      );
    }

    if (conditionType === 'assignee_equals') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignee Email or ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.conditionConfig?.assignee || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, assignee: e.target.value }
              })}
              placeholder="Enter assignee email or user ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the assignee's email address or user ID
            </p>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Checks if the task is assigned to the specified user
          </div>
        </div>
      );
    }

    if (conditionType === 'task_overdue') {
      return (
        <div className="space-y-4">
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            This condition checks if the task's due date has passed. No additional configuration needed.
          </div>
        </div>
      );
    }

    if (conditionType === 'custom_field') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Field <span className="text-red-500">*</span>
            </label>
            <select
              value={config.conditionConfig?.field_name || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, field_name: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select Field...</option>
              {customFields.map(field => (
                <option key={field.id} value={field.name}>{field.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Equals Value <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.conditionConfig?.value || ''}
              onChange={(e) => setConfig({
                ...config,
                conditionConfig: { ...config.conditionConfig, value: e.target.value }
              })}
              placeholder="Enter value..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderActionConfig = () => {
    const actionType = config.actionType || node?.data?.actionType;

    if (actionType === 'webhook') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={config.actionConfig?.url || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, url: e.target.value }
              })}
              placeholder="https://example.com/webhook"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bearer Token <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.auth?.token || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { 
                  ...config.actionConfig, 
                  auth: { type: 'bearer', token: e.target.value }
                }
              })}
              placeholder="your-secret-token"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Headers <span className="text-gray-500">(JSON)</span>
            </label>
            <textarea
              value={JSON.stringify(config.actionConfig?.headers || {}, null, 2)}
              onChange={(e) => {
                try {
                  const headers = JSON.parse(e.target.value);
                  setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, headers }
                  });
                } catch (err) {
                  // Invalid JSON, keep previous value
                }
              }}
              rows={3}
              placeholder='{"Content-Type": "application/json"}'
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 font-mono text-sm"
            />
          </div>

          {/* Payload Builder Mode Toggle */}
          <div className="flex items-center space-x-4 mb-2">
            <label className="block text-sm font-medium text-gray-700">Payload Configuration</label>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, payloadMode: 'builder' }
                })}
                className={`px-3 py-1 text-sm rounded-md ${
                  (config.actionConfig?.payloadMode || 'builder') === 'builder'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Field Selector
              </button>
              <button
                type="button"
                onClick={() => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, payloadMode: 'manual' }
                })}
                className={`px-3 py-1 text-sm rounded-md ${
                  config.actionConfig?.payloadMode === 'manual'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Manual JSON
              </button>
            </div>
          </div>

          {(config.actionConfig?.payloadMode || 'builder') === 'builder' ? (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              {/* Task Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task Fields
                  {taskCustomFields.length > 0 && (
                    <span className="text-gray-400 ml-1 text-xs">
                      ({21 + taskCustomFields.length} total, {taskCustomFields.length} custom)
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {[
                    { key: 'task.id', label: 'Task ID' },
                    { key: 'task.name', label: 'Task Name' },
                    { key: 'task.description', label: 'Description' },
                    { key: 'task.status', label: 'Stage' },
                    { key: 'task.priority', label: 'Priority' },
                    { key: 'task.category', label: 'Category' },
                    { key: 'task.due_date', label: 'Due Date' },
                    { key: 'task.start_date', label: 'Start Date' },
                    { key: 'task.assignees', label: 'Assignees' },
                    { key: 'task.tags', label: 'Tags' },
                    { key: 'task.estimated_effort', label: 'Estimated Effort' },
                    { key: 'task.percent_complete', label: 'Percent Complete' },
                    { key: 'task.is_milestone', label: 'Is Milestone' },
                    { key: 'task.show_in_portal', label: 'Show in Portal' },
                    { key: 'task.deliverables', label: 'Deliverables (full)' },
                    { key: 'task.deliverable_links', label: 'Deliverable Links' },
                    { key: 'task.deliverable_attachments', label: 'Deliverable Attachments' },
                    { key: 'task.review_notes', label: 'Deliverable Review Notes' },
                    { key: 'task.attachments', label: 'Task Attachments / Uploads' },
                    { key: 'task.comments', label: 'Task Comments' },
                    { key: 'task.comments_count', label: 'Task Comments Count' },
                    // Add task custom fields
                    ...taskCustomFields.map(field => ({
                      key: `task.custom_fields.${field.name}`,
                      label: `${field.label || field.name} (custom)`,
                      customFieldId: field.id
                    }))
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-1 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(config.actionConfig?.selectedFields || []).includes(field.key)}
                        onChange={(e) => {
                          const current = config.actionConfig?.selectedFields || [];
                          const updated = e.target.checked
                            ? [...current, field.key]
                            : current.filter(f => f !== field.key);
                          setConfig({
                            ...config,
                            actionConfig: { ...config.actionConfig, selectedFields: updated }
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Client Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Fields
                  {clientCustomFields.length > 0 && (
                    <span className="text-gray-400 ml-1 text-xs">
                      ({9 + clientCustomFields.filter(f => !['client_name', 'name', 'email', 'phone', 'website'].includes(f.variable_name || f.name.toLowerCase().replace(/\s+/g, '_'))).length} total, {clientCustomFields.filter(f => !['client_name', 'name', 'email', 'phone', 'website'].includes(f.variable_name || f.name.toLowerCase().replace(/\s+/g, '_'))).length} custom)
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {[
                    { key: 'client.id', label: 'Client ID' },
                    { key: 'client.name', label: 'Client Name' },
                    { key: 'client.email', label: 'Email' },
                    { key: 'client.phone', label: 'Phone' },
                    { key: 'client.website', label: 'Website' },
                    { key: 'client.address', label: 'Address' },
                    { key: 'client.industry', label: 'Industry' },
                    { key: 'client.company_size', label: 'Company Size' },
                    { key: 'client.source', label: 'Source' },
                    // Add client custom fields (filter out duplicates of standard fields)
                    ...clientCustomFields
                      .filter(field => {
                        const varName = field.variable_name || field.name.toLowerCase().replace(/\s+/g, '_');
                        // Exclude predefined fields that duplicate standard client fields
                        return !['client_name', 'name', 'email', 'phone', 'website'].includes(varName);
                      })
                      .map(field => ({
                        key: `client.custom_fields.${field.name}`,
                        label: `${field.label || field.name} (custom)`,
                        customFieldId: field.id
                      }))
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-1 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(config.actionConfig?.selectedFields || []).includes(field.key)}
                        onChange={(e) => {
                          const current = config.actionConfig?.selectedFields || [];
                          const updated = e.target.checked
                            ? [...current, field.key]
                            : current.filter(f => f !== field.key);
                          setConfig({
                            ...config,
                            actionConfig: { ...config.actionConfig, selectedFields: updated }
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Project Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Fields
                  {projectCustomFields.length > 0 && (
                    <span className="text-gray-400 ml-1 text-xs">
                      ({5 + projectCustomFields.length} total, {projectCustomFields.length} custom)
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {[
                    { key: 'project.id', label: 'Project ID' },
                    { key: 'project.name', label: 'Project Name' },
                    { key: 'project.status', label: 'Status' },
                    { key: 'project.due_date', label: 'Due Date' },
                    { key: 'project.category', label: 'Category' },
                    // Add project custom fields
                    ...projectCustomFields.map(field => ({
                      key: `project.custom_fields.${field.name}`,
                      label: `${field.label || field.name} (custom)`,
                      customFieldId: field.id
                    }))
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-1 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(config.actionConfig?.selectedFields || []).includes(field.key)}
                        onChange={(e) => {
                          const current = config.actionConfig?.selectedFields || [];
                          const updated = e.target.checked
                            ? [...current, field.key]
                            : current.filter(f => f !== field.key);
                          setConfig({
                            ...config,
                            actionConfig: { ...config.actionConfig, selectedFields: updated }
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Portal Submission Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portal Submission Fields
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                  {[
                    { key: 'portal.request_type', label: 'Request Type' },
                    { key: 'portal.request_subtype', label: 'Request Subtype' },
                    { key: 'portal.title', label: 'Portal Title' },
                    { key: 'portal.description', label: 'Portal Description' },
                    { key: 'portal.priority', label: 'Portal Priority' },
                    { key: 'portal.attachments_count', label: 'Portal Attachments Count' },
                    { key: 'portal.form_version', label: 'Portal Form Version' },
                    { key: 'portal.form_data', label: 'Portal Form Data (object)' },
                    { key: 'portal.form_data_flat', label: 'Portal Form Data Flat' },
                    { key: 'portal.request_in_scope', label: 'Request In Scope' },
                    { key: 'portal.matching_scope', label: 'Matching Scope' },
                    ...portalFields.map((f) => ({
                      key: f.path || f.variable?.replace('{{', '').replace('}}', ''),
                      label: `Portal Field: ${f.label || f.name}`
                    })).filter(f => !!f.key)
                  ].map(field => (
                    <label key={field.key} className="flex items-center p-1 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(config.actionConfig?.selectedFields || []).includes(field.key)}
                        onChange={(e) => {
                          const current = config.actionConfig?.selectedFields || [];
                          const updated = e.target.checked
                            ? [...current, field.key]
                            : current.filter(f => f !== field.key);
                          setConfig({
                            ...config,
                            actionConfig: { ...config.actionConfig, selectedFields: updated }
                          });
                        }}
                        className="mr-2"
                      />
                      <span className="text-xs">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Fields section removed - now integrated into each entity section above */}

              {/* Metadata Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Include Metadata</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.actionConfig?.includeTimestamp !== false}
                      onChange={(e) => setConfig({
                        ...config,
                        actionConfig: { ...config.actionConfig, includeTimestamp: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">Timestamp</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.actionConfig?.includeTriggerType !== false}
                      onChange={(e) => setConfig({
                        ...config,
                        actionConfig: { ...config.actionConfig, includeTriggerType: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">Trigger Type</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.actionConfig?.includeWorkflowInfo || false}
                      onChange={(e) => setConfig({
                        ...config,
                        actionConfig: { ...config.actionConfig, includeWorkflowInfo: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm">Workflow Info</span>
                  </label>
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payload Preview</label>
                <pre className="bg-gray-800 text-green-400 p-3 rounded-md text-xs overflow-x-auto max-h-40">
                  {JSON.stringify(
                    buildWebhookPayloadPreview(config.actionConfig),
                    null, 2
                  )}
                </pre>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body Template <span className="text-gray-500">(JSON with variables)</span>
              </label>
              <textarea
                value={JSON.stringify(config.actionConfig?.body || {
                  event: "task_updated",
                  task: {
                    id: "{{task.id}}",
                    name: "{{task.name}}",
                    stage: "{{task.stage}}"
                  }
                }, null, 2)}
                onChange={(e) => {
                  try {
                    const body = JSON.parse(e.target.value);
                    setConfig({
                      ...config,
                      actionConfig: { ...config.actionConfig, body }
                    });
                  } catch (err) {
                    // Invalid JSON
                  }
                }}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use {'{{variable}}'} syntax. Available: task.id, task.name, task.stage, client.name, project.name, etc.
              </p>
            </div>
          )}
        </div>
      );
    }

    if (actionType === 'email') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Send From <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.send_from || 'system'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, send_from: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="system">System Email (SendGrid Template)</option>
              <option value="personal">Personal Email (Your Connected Account)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {config.actionConfig?.send_from === 'system' 
                ? 'Uses branded system template with SendGrid' 
                : 'Sends from your connected Gmail/Outlook account'}
            </p>
          </div>

          {config.actionConfig?.send_from === 'personal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email Account <span className="text-red-500">*</span>
              </label>
              <select
                value={config.actionConfig?.from_email_account || 'primary'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, from_email_account: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              >
                <option value="primary">Primary Connected Email</option>
                <option value="task_assignee">Task Assignee's Email (Primary Assignee)</option>
                <option value="project_manager">Project Manager's Email</option>
                <option value="account_owner">Account Owner's Email</option>
                <option value="specific">Specific Email...</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {config.actionConfig?.from_email_account === 'task_assignee' && 
                  'For tasks with multiple assignees, uses the first assignee\'s email'}
                {config.actionConfig?.from_email_account === 'project_manager' && 
                  'Uses the email of the project manager associated with this task'}
                {config.actionConfig?.from_email_account === 'account_owner' && 
                  'Uses the team/account owner\'s email'}
              </p>
            </div>
          )}

          {config.actionConfig?.send_from === 'personal' && config.actionConfig?.from_email_account === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={config.actionConfig?.specific_from_email || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, specific_from_email: e.target.value }
                })}
                placeholder="your.email@company.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be a connected email account
              </p>
            </div>
          )}

          {config.actionConfig?.send_from === 'personal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fallback Email <span className="text-gray-500">(if primary fails)</span>
                </label>
                <select
                  value={config.actionConfig?.fallback_email || 'account_owner'}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, fallback_email: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="account_owner">Account Owner's Email</option>
                  <option value="system">System Email (SendGrid)</option>
                  <option value="none">No Fallback (Fail if primary unavailable)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Used when the primary selected email account is not connected or fails
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
                <Info className="w-4 h-4 inline mr-2" />
                <strong>Email Sending Logic:</strong> 
                {config.actionConfig?.fallback_email === 'account_owner' 
                  ? ' Tries primary → Account Owner → System SendGrid'
                  : config.actionConfig?.fallback_email === 'system'
                    ? ' Tries primary → System SendGrid'
                    : ' Only tries primary (workflow fails if unavailable)'}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Send To <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.recipient_type || 'assignee'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, recipient_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="assignee">Task Assignee</option>
              <option value="client">Client Contact (Primary)</option>
              <option value="all_client_contacts">All Client's Contacts</option>
              <option value="custom">Custom Email</option>
            </select>
          </div>

          {config.actionConfig?.recipient_type === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={config.actionConfig?.custom_email || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, custom_email: e.target.value }
                })}
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Subject <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={(value) => insertAtCursor(emailSubjectRef, 'actionConfig.subject', value)}
                includeAI={true}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookVariables}
              />
            </div>
            <input
              ref={emailSubjectRef}
              type="text"
              value={config.actionConfig?.subject || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, subject: e.target.value }
              })}
              placeholder="Task Update: {{task.name}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Message Body <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={(value) => insertAtCursor(emailBodyRef, 'actionConfig.body', value)}
                includeAI={true}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookVariables}
              />
            </div>
            <textarea
              ref={emailBodyRef}
              value={config.actionConfig?.body || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, body: e.target.value }
              })}
              rows={6}
              placeholder="Task {{task.name}} has been moved to {{task.stage}}."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Tip:</strong> If an AI Generate node runs before this email action, you can use {'{{'} ai_content.subject {'}}'} and {'{{'} ai_content.body {'}}'} to insert the generated content.
          </div>
        </div>
      );
    }

    if (actionType === 'ai_generate') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What to Generate <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.content_type || 'email'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, content_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="email">Email Content (Subject + Body)</option>
              <option value="summary">Task Summary</option>
              <option value="update">Status Update</option>
              <option value="custom">Custom Generation</option>
            </select>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Additional Instructions <span className="text-gray-500">(optional but recommended)</span>
              </label>
              <VariablePicker 
                onInsert={(value) => insertAtCursor(instructionsRef, 'actionConfig.instructions', value)}
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookVariables}
              />
            </div>
            <textarea
              ref={instructionsRef}
              value={config.actionConfig?.instructions || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, instructions: e.target.value }
              })}
              rows={4}
              placeholder="Example: Write in a friendly tone, mention the deadline, include next steps..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Customize the AI output with specific instructions. Use the "Insert Variable" button to add task/client data.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Audience <span className="text-gray-500">(helps AI adjust tone)</span>
            </label>
            <select
              value={config.actionConfig?.audience || 'client'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, audience: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="client">Client (Professional & Clear)</option>
              <option value="internal">Internal Team (Technical & Brief)</option>
              <option value="executive">Executive (High-level Summary)</option>
            </select>
          </div>

          <div className="bg-purple-50 p-4 rounded-md text-sm space-y-2">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong className="text-purple-900">How AI Generation Works:</strong>
                <p className="text-purple-800 mt-1">
                  AI uses task data (name, description, status, category) plus your custom instructions to generate content. 
                  The output is saved as <code className="bg-purple-100 px-1 rounded">ai_content.subject</code> and <code className="bg-purple-100 px-1 rounded">ai_content.body</code> 
                  which can be used in subsequent email actions.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Best Practice:</strong> Add specific instructions about tone, length, and key points to include for better AI output quality.
          </div>
        </div>
      );
    }

    if (actionType === 'update_assignees') {
      const selectedEmails = config.actionConfig?.user_emails ? config.actionConfig.user_emails.split(',').map(e => e.trim()) : [];
      const assigneeSource = config.actionConfig?.assignee_source || 'specific_users';
      
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.assignee_action || 'add'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, assignee_action: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="add">Add Assignee(s)</option>
              <option value="remove">Remove Assignee(s)</option>
              <option value="replace">Replace All Assignees</option>
              <option value="clear">Clear All Assignees</option>
            </select>
          </div>

          {config.actionConfig?.assignee_action !== 'clear' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assignee Source <span className="text-red-500">*</span>
                </label>
                <select
                  value={assigneeSource}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, assignee_source: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="specific_users">Specific Team Members</option>
                  <option value="project_manager">Project Manager</option>
                  <option value="task_creator">Task Creator</option>
                  <option value="trigger_user">User Who Triggered Workflow</option>
                  <option value="current_assignees">Current Assignees (from trigger task)</option>
                  <option value="client_account_manager">Client Account Manager</option>
                </select>
              </div>

              {assigneeSource === 'specific_users' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Team User(s) <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-60 overflow-y-auto">
                    {teamUsers.map(user => (
                      <label key={user.id} className="flex items-center py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEmails.includes(user.email)}
                          onChange={(e) => {
                            let newEmails;
                            if (e.target.checked) {
                              newEmails = [...selectedEmails, user.email];
                            } else {
                              newEmails = selectedEmails.filter(email => email !== user.email);
                            }
                            setConfig({
                              ...config,
                              actionConfig: { ...config.actionConfig, user_emails: newEmails.join(', ') }
                            });
                          }}
                          className="mr-3 h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                        </div>
                      </label>
                    ))}
                    {teamUsers.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">Loading team members...</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {selectedEmails.length} user(s)
                  </p>
                </div>
              )}

              {assigneeSource !== 'specific_users' && (
                <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700">
                  <Info className="w-4 h-4 inline mr-2" />
                  {assigneeSource === 'project_manager' && 'Will use the project manager of the task\'s project'}
                  {assigneeSource === 'task_creator' && 'Will use the user who created the triggering task'}
                  {assigneeSource === 'trigger_user' && 'Will use the user whose action triggered this workflow'}
                  {assigneeSource === 'current_assignees' && 'Will use the assignees from the task that triggered the workflow'}
                  {assigneeSource === 'client_account_manager' && 'Will use the account manager assigned to the client'}
                </div>
              )}
            </>
          )}

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Actions:</strong>
            <ul className="list-disc ml-6 mt-1">
              <li><strong>Add:</strong> Adds user(s) to existing assignees</li>
              <li><strong>Remove:</strong> Removes user(s) from assignees</li>
              <li><strong>Replace:</strong> Replaces all assignees with specified user(s)</li>
              <li><strong>Clear:</strong> Removes all assignees from the task</li>
            </ul>
          </div>
        </div>
      );
    }

    if (actionType === 'change_category') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Category <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.category || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, category: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Choose a category...</option>
              {taskCategories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
            {taskCategories.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">Loading categories...</p>
            )}
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Changes the task's category to the selected value
          </div>
        </div>
      );
    }

    if (actionType === 'update_project_manager') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Manager Selection <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.pm_selection || 'specific'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, pm_selection: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="specific">Specific Team Member</option>
              <option value="task_assignee">First Task Assignee</option>
              <option value="triggering_user">User Who Triggered</option>
            </select>
          </div>

          {(config.actionConfig?.pm_selection === 'specific' || !config.actionConfig?.pm_selection) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Project Manager <span className="text-red-500">*</span>
              </label>
              <select
                value={config.actionConfig?.project_manager_id || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, project_manager_id: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Select a team member...</option>
                {teamUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Updates the project manager on the project associated with the triggering task
          </div>
        </div>
      );
    }

    if (actionType === 'create_task') {
      // Handle template selection
      const handleTemplateSelect = (templateId) => {
        if (!templateId) {
          // Clear template selection
          setConfig({
            ...config,
            actionConfig: { 
              ...config.actionConfig, 
              template_id: null,
              use_template: false
            }
          });
          return;
        }
        
        const template = taskTemplates.find(t => t.id === templateId);
        if (template) {
          setConfig({
            ...config,
            actionConfig: { 
              ...config.actionConfig, 
              template_id: templateId,
              use_template: true,
              // Optionally pre-fill fields from template (user can override)
              task_name: config.actionConfig?.task_name || template.name,
              task_description: config.actionConfig?.task_description || template.description || '',
              category: config.actionConfig?.category || template.category || '',
              priority: config.actionConfig?.priority || template.priority || 'medium'
            }
          });
        }
      };

      return (
        <div className="space-y-4">
          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Use Task Template (Optional)
            </label>
            <select
              value={config.actionConfig?.template_id || ''}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">No template - configure manually</option>
              {taskTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.category ? `(${template.category})` : ''}
                </option>
              ))}
            </select>
            {config.actionConfig?.template_id && (
              <p className="text-xs text-green-600 mt-1">
                ✓ Template selected - fields below will use template defaults (you can override)
              </p>
            )}
          </div>

          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Task Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.task_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, task_name: e.target.value }
              })}
              placeholder="Enter task name (supports {{variables}})"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Variables: {'{{task.name}}'}, {'{{client.name}}'}, {'{{webhook_payload.task.name}}'}, {'{{webhook_payload.custom_data.field}}'}
            </p>
          </div>

          {/* Task Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={config.actionConfig?.task_description || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, task_description: e.target.value }
              })}
              placeholder="Enter task description (supports {{variables}})"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              For webhook triggers, use {'{{webhook_payload.description}}'} or any custom field path
            </p>
          </div>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project
            </label>
            <select
              value={config.actionConfig?.project_source || 'same'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, project_source: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="same">Same as Triggering Task</option>
              <option value="specific">Specific Project</option>
            </select>
          </div>

          {config.actionConfig?.project_source === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.actionConfig?.project_name || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, project_name: e.target.value }
                })}
                placeholder="e.g., General, Website Management"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Matches by name under the client from the trigger
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={config.actionConfig?.priority || 'normal'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, priority: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={config.actionConfig?.category || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, category: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">No Category</option>
                {taskCategories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assignees</label>
            <select
              value={config.actionConfig?.assignee_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, assignee_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="none">No Assignees</option>
              <option value="project_manager">Project Manager</option>
              <option value="same_as_trigger">Same as Triggering Task</option>
              <option value="specific">Specific Team Members</option>
            </select>
          </div>

          {config.actionConfig?.assignee_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Assignees</label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                {teamUsers.map(user => (
                  <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(config.actionConfig?.assignees || []).includes(user.id)}
                      onChange={(e) => {
                        const current = config.actionConfig?.assignees || [];
                        const updated = e.target.checked
                          ? [...current, user.id]
                          : current.filter(id => id !== user.id);
                        setConfig({
                          ...config,
                          actionConfig: { ...config.actionConfig, assignees: updated }
                        });
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm">{user.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Stage Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Initial Stage</label>
            <select
              value={config.actionConfig?.stage_type || 'first'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, stage_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="first">First Stage (Default)</option>
              <option value="specific">Specific Stage</option>
            </select>
          </div>

          {config.actionConfig?.stage_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stage Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.actionConfig?.stage_name || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, stage_name: e.target.value }
                })}
                placeholder="e.g., To Do, In Progress, Review"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Matches by name in the selected project
              </p>
            </div>
          )}

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
            <select
              value={config.actionConfig?.due_date_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, due_date_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="none">No Due Date</option>
              <option value="offset">Days from Today</option>
              <option value="specific">Specific Date</option>
            </select>
          </div>

          {config.actionConfig?.due_date_type === 'offset' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days from Today</label>
              <input
                type="number"
                value={config.actionConfig?.due_date_offset || 0}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date_offset: e.target.value }
                })}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}

          {config.actionConfig?.due_date_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
              <input
                type="date"
                value={config.actionConfig?.due_date || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Additional Options */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Effort</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={config.actionConfig?.estimated_effort_value || ''}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, estimated_effort_value: e.target.value ? parseInt(e.target.value) : null }
                  })}
                  placeholder="e.g., 2"
                  min="1"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <select
                  value={config.actionConfig?.estimated_effort_unit || 'hours'}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, estimated_effort_unit: e.target.value }
                  })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.actionConfig?.is_milestone || false}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, is_milestone: e.target.checked }
                  })}
                  className="mr-2"
                />
                <span className="text-sm">Milestone</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.actionConfig?.show_in_portal || false}
                  onChange={(e) => setConfig({
                    ...config,
                    actionConfig: { ...config.actionConfig, show_in_portal: e.target.checked }
                  })}
                  className="mr-2"
                />
                <span className="text-sm">Show in Portal</span>
              </label>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Creates a new task with the specified configuration
          </div>
        </div>
      );
    }

    // Add Subtask Action
    if (actionType === 'add_subtask') {
      return (
        <div className="space-y-4">
          {/* Important Note */}
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>Requires Task Context:</strong> This action creates a subtask under the task from the workflow trigger. 
            The workflow must be triggered by a task-related event.
          </div>

          {/* Subtask Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtask Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.subtask_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, subtask_name: e.target.value }
              })}
              placeholder="Enter subtask name (supports {{variables}})"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Variables: {'{{task.name}}'}, {'{{client.name}}'}, {'{{user.name}}'}
            </p>
          </div>

          {/* Subtask Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={config.actionConfig?.subtask_description || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, subtask_description: e.target.value }
              })}
              placeholder="Enter subtask description (supports {{variables}})"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={config.actionConfig?.status || 'To Do'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, status: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="To Do">To Do</option>
                <option value="In Progress">In Progress</option>
                <option value="In Review">In Review</option>
                <option value="Done">Done</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={config.actionConfig?.priority || 'Normal'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, priority: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={config.actionConfig?.category || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, category: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">No Category</option>
              {taskCategories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assignee Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assignees</label>
            <select
              value={config.actionConfig?.assignee_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, assignee_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="none">No Assignees</option>
              <option value="same_as_parent">Same as Parent Task</option>
              <option value="project_manager">Project Manager</option>
              <option value="specific">Specific Team Members</option>
            </select>
          </div>

          {config.actionConfig?.assignee_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Assignees</label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                {teamUsers.map(user => (
                  <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(config.actionConfig?.assignees || []).includes(user.id)}
                      onChange={(e) => {
                        const currentAssignees = config.actionConfig?.assignees || [];
                        const newAssignees = e.target.checked
                          ? [...currentAssignees, user.id]
                          : currentAssignees.filter(id => id !== user.id);
                        setConfig({
                          ...config,
                          actionConfig: { ...config.actionConfig, assignees: newAssignees }
                        });
                      }}
                      className="rounded text-green-600 focus:ring-green-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">{user.name || user.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
            <select
              value={config.actionConfig?.due_date_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, due_date_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="none">No Due Date</option>
              <option value="same_as_parent">Same as Parent Task</option>
              <option value="offset">Days from Now</option>
              <option value="specific">Specific Date</option>
            </select>
          </div>

          {config.actionConfig?.due_date_type === 'offset' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days from Now
              </label>
              <input
                type="number"
                value={config.actionConfig?.due_date_offset || 7}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date_offset: parseInt(e.target.value) || 0 }
                })}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          )}

          {config.actionConfig?.due_date_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={config.actionConfig?.due_date || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Estimated Effort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Effort</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={config.actionConfig?.estimated_effort_value || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, estimated_effort_value: e.target.value ? parseInt(e.target.value) : null }
                })}
                placeholder="e.g., 2"
                min="1"
                className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <select
                value={config.actionConfig?.estimated_effort_unit || 'hours'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, estimated_effort_unit: e.target.value }
                })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>

          {/* Info */}
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            Creates a subtask under the triggering task. The subtask inherits the parent's project, client, and team.
          </div>
        </div>
      );
    }

    // Update Status Action
    if (actionType === 'update_status') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Status <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.status || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, status: e.target.value }
              })}
              placeholder="e.g., In Progress, Completed, On Hold"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Updates the task's status to the specified value
          </div>
        </div>
      );
    }

    // Update Priority Action
    if (actionType === 'update_priority') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Priority <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.priority || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, priority: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="">Select priority...</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Updates the task's priority level
          </div>
        </div>
      );
    }

    // Move to Stage Action
    if (actionType === 'move_to_stage') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Stage <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.stage_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, stage_name: e.target.value }
              })}
              placeholder="e.g., In Review, Done, QA"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the stage name - will match by name within the task's project
            </p>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Moves the task to the specified project stage
          </div>
        </div>
      );
    }

    // Change Project Action
    if (actionType === 'change_project') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.project_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, project_name: e.target.value }
              })}
              placeholder="e.g., Active Projects, Completed Work"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the exact project name - will match by name within the same client
            </p>
          </div>
          <div className="bg-purple-50 p-3 rounded-md text-sm text-purple-800">
            <Info className="w-4 h-4 inline mr-2" />
            Transfers the task to another project belonging to the same client. The project must exist and match by name (case-insensitive).
          </div>
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Note: Tasks can only be moved to projects within the same client. Cross-client transfers are not allowed.
          </div>
        </div>
      );
    }

    // Set Due Date Action
    if (actionType === 'set_due_date') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Due Date Type <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.date_type || 'offset'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, date_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="offset">Days from Today</option>
              <option value="specific">Specific Date</option>
              <option value="clear">Clear Due Date</option>
            </select>
          </div>
          {config.actionConfig?.date_type === 'offset' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days from Today
              </label>
              <input
                type="number"
                value={config.actionConfig?.days_offset || 7}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, days_offset: parseInt(e.target.value) }
                })}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
          {config.actionConfig?.date_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={config.actionConfig?.specific_date || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, specific_date: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Sets or updates the task's due date
          </div>
        </div>
      );
    }

    // Add Comment Action
    if (actionType === 'add_comment') {
      const insertVariableToComment = (variable) => {
        const currentContent = config.actionConfig?.content || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, content: currentContent + variable }
        });
      };

      return (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Comment Content <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToComment} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <textarea
              value={config.actionConfig?.content || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, content: e.target.value }
              })}
              placeholder="Enter comment text... Click the tag icon above to insert variables"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_system_comment"
              checked={config.actionConfig?.is_system_comment || false}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, is_system_comment: e.target.checked }
              })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_system_comment" className="text-sm text-gray-700">
              Mark as system comment (won't notify users)
            </label>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Adds a comment to the task
          </div>
        </div>
      );
    }

    // Send Notification Action
    if (actionType === 'send_notification') {
      // Helper function to insert variable into notification title
      const insertVariableToNotificationTitle = (variable) => {
        const currentTitle = config.actionConfig?.title || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, title: currentTitle + variable }
        });
      };

      // Helper function to insert variable into notification message
      const insertVariableToNotificationMessage = (variable) => {
        const currentMessage = config.actionConfig?.message || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, message: currentMessage + variable }
        });
      };

      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notify
            </label>
            <select
              value={config.actionConfig?.notify_type || 'assignees'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, notify_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="assignees">Task Assignees</option>
              <option value="project_manager">Project Manager</option>
              <option value="specific_users">Specific Users</option>
              <option value="team_admins">Team Admins</option>
            </select>
          </div>
          {config.actionConfig?.notify_type === 'specific_users' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Users to Notify
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                {teamUsers.length > 0 ? (
                  teamUsers.map(user => (
                    <label key={user.id} className="flex items-center gap-3 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(config.actionConfig?.selected_user_ids || []).includes(user.id)}
                        onChange={(e) => {
                          const currentIds = config.actionConfig?.selected_user_ids || [];
                          const newIds = e.target.checked
                            ? [...currentIds, user.id]
                            : currentIds.filter(id => id !== user.id);
                          setConfig({
                            ...config,
                            actionConfig: { ...config.actionConfig, selected_user_ids: newIds }
                          });
                        }}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </label>
                  ))
                ) : (
                  <div className="text-gray-500 text-sm py-2">Loading team members...</div>
                )}
              </div>
              {(config.actionConfig?.selected_user_ids || []).length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {(config.actionConfig?.selected_user_ids || []).length} user(s) selected
                </p>
              )}
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Notification Title <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToNotificationTitle} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <input
              type="text"
              value={config.actionConfig?.title || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, title: e.target.value }
              })}
              placeholder="e.g., Task Update: {{task.name}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Message <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToNotificationMessage} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <textarea
              value={config.actionConfig?.message || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, message: e.target.value }
              })}
              placeholder="Enter notification message... Click the tag icon above to insert variables"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Sends an in-app notification to the selected users. Use the tag icon to insert dynamic variables.
          </div>
        </div>
      );
    }

    // Add Tag Action
    if (actionType === 'add_tag') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag(s) to Add <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.tags || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, tags: e.target.value }
              })}
              placeholder="e.g., urgent, needs-review, client-request"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated list of tags to add
            </p>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Adds the specified tags to the task
          </div>
        </div>
      );
    }

    // Remove Tag Action
    if (actionType === 'remove_tag') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag(s) to Remove <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.tags || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, tags: e.target.value }
              })}
              placeholder="e.g., pending, needs-attention"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated list of tags to remove
            </p>
          </div>
          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Removes the specified tags from the task
          </div>
        </div>
      );
    }

    // Wait Action
    if (actionType === 'wait') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wait Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <input
                type="number"
                value={config.actionConfig?.duration || 5}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, duration: parseInt(e.target.value) }
                })}
                min="1"
                max="1440"
                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                required
              />
              <select
                value={config.actionConfig?.unit || 'minutes'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, unit: e.target.value }
                })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>
          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            Pauses the workflow for the specified duration before continuing to the next action
          </div>
        </div>
      );
    }

    // GPT Action (General Purpose)
    if (actionType === 'gpt_action') {
      const insertVariableToPrompt = (variable) => {
        const currentPrompt = config.actionConfig?.prompt || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, prompt: currentPrompt + variable }
        });
      };

      return (
        <div className="space-y-4">
          {/* Info banner about multiple GPT actions */}
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Multiple GPT Actions:</strong> Each GPT action in your workflow automatically stores its result in a numbered variable 
            (<code className="bg-blue-100 px-1 rounded">{'{{gpt_result_1}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{gpt_result_2}}'}</code>, etc.) 
            so you can chain multiple GPT calls and use each result in subsequent actions.
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Prompt <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToPrompt} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <textarea
              value={config.actionConfig?.prompt || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, prompt: e.target.value }
              })}
              placeholder="Enter your GPT prompt... Click the tag icon above to insert variables (including results from previous GPT actions)"
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={config.actionConfig?.model || 'gpt-4o'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, model: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="gpt-4o">GPT-4o (Recommended)</option>
              <option value="gpt-4o-mini">GPT-4o Mini (Fast)</option>
              <option value="gpt-4-turbo">GPT-4 Turbo (Powerful)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={config.actionConfig?.max_tokens || 500}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, max_tokens: parseInt(e.target.value) }
              })}
              min="50"
              max="4000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Variable Name <span className="text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={config.actionConfig?.result_variable || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, result_variable: e.target.value }
              })}
              placeholder="e.g., classification_result, summary, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Results are automatically stored in <code className="bg-gray-100 px-1 rounded">{'{{gpt_result_1}}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{{gpt_result_2}}'}</code>, etc.
              Add a custom name for easier reference (e.g., <code className="bg-gray-100 px-1 rounded">{'{{classification_result}}'}</code>)
            </p>
          </div>
          <div className="bg-purple-50 p-3 rounded-md text-sm text-purple-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Tip:</strong> Chain multiple GPT actions like HighLevel: GPT Action 1 classifies the request → GPT Action 2 uses that classification to generate a response.
          </div>
        </div>
      );
    }

    // Create Project Action
    if (actionType === 'create_project') {
      // Handle project template selection
      const handleProjectTemplateSelect = (templateId) => {
        if (!templateId) {
          setConfig({
            ...config,
            actionConfig: { 
              ...config.actionConfig, 
              template_id: null,
              use_template: false
            }
          });
          return;
        }
        
        const template = projectTemplates.find(t => t.id === templateId);
        if (template) {
          setConfig({
            ...config,
            actionConfig: { 
              ...config.actionConfig, 
              template_id: templateId,
              use_template: true,
              project_name: config.actionConfig?.project_name || template.name,
              project_description: config.actionConfig?.project_description || template.description || '',
              category: config.actionConfig?.category || template.category || 'General',
              complexity: config.actionConfig?.complexity || template.complexity || 'Medium',
              estimated_duration: config.actionConfig?.estimated_duration || template.estimated_duration || '',
              estimated_duration_metric: config.actionConfig?.estimated_duration_metric || template.estimated_duration_metric || 'days'
            }
          });
        }
      };

      // Helper function to insert variable into project name
      const insertVariableToProjectName = (variable) => {
        const currentName = config.actionConfig?.project_name || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, project_name: currentName + variable }
        });
      };

      // Helper function to insert variable into project description
      const insertVariableToProjectDescription = (variable) => {
        const currentDesc = config.actionConfig?.project_description || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, project_description: currentDesc + variable }
        });
      };

      return (
        <div className="space-y-4">
          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Use Project Template (Optional)
            </label>
            <select
              value={config.actionConfig?.template_id || ''}
              onChange={(e) => handleProjectTemplateSelect(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">No template - configure manually</option>
              {projectTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.category ? `(${template.category})` : ''}
                </option>
              ))}
            </select>
            {config.actionConfig?.template_id && (
              <p className="text-xs text-green-600 mt-1">
                ✓ Template selected - fields below will use template defaults (you can override). Template stages and default tasks will be applied.
              </p>
            )}
          </div>

          {/* Project Name */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Project Name <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToProjectName} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <input
              type="text"
              value={config.actionConfig?.project_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, project_name: e.target.value }
              })}
              placeholder="e.g., {{client.name}} - Website Redesign"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Click the tag icon to insert variables like {'{{client.name}}'}, {'{{task.name}}'}
            </p>
          </div>

          {/* Project Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <VariablePicker 
                onInsert={insertVariableToProjectDescription} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <textarea
              value={config.actionConfig?.project_description || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, project_description: e.target.value }
              })}
              placeholder="Enter project description (supports {{variables}})"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Client Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client
            </label>
            <select
              value={config.actionConfig?.client_source || 'from_trigger'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, client_source: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="from_trigger">From Trigger Context (Task/Project Client)</option>
              <option value="from_webhook">From Webhook Payload</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The project will be created under this client
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={config.actionConfig?.category || 'General'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, category: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {projectCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Complexity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Complexity</label>
              <select
                value={config.actionConfig?.complexity || 'Medium'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, complexity: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>

          {/* Estimated Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Duration
            </label>
            <div className="flex gap-3">
              <input
                type="number"
                value={config.actionConfig?.estimated_duration || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, estimated_duration: e.target.value }
                })}
                min="1"
                placeholder="Duration"
                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
              <select
                value={config.actionConfig?.estimated_duration_metric || 'days'}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, estimated_duration_metric: e.target.value }
                })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Due Date
            </label>
            <select
              value={config.actionConfig?.due_date_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, due_date_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="none">No Due Date</option>
              <option value="offset">Days from Now</option>
              <option value="specific">Specific Date</option>
            </select>
          </div>

          {config.actionConfig?.due_date_type === 'offset' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days from Now
              </label>
              <input
                type="number"
                value={config.actionConfig?.due_date_offset || 30}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date_offset: parseInt(e.target.value) }
                })}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          {config.actionConfig?.due_date_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date
              </label>
              <input
                type="date"
                value={config.actionConfig?.due_date || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, due_date: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          {/* Project Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project Manager</label>
            <select
              value={config.actionConfig?.project_manager_type || 'none'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, project_manager_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="none">No Project Manager</option>
              <option value="workflow_user">User who triggered workflow</option>
              <option value="specific">Specific Team Member</option>
            </select>
          </div>

          {config.actionConfig?.project_manager_type === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Project Manager</label>
              <select
                value={config.actionConfig?.project_manager_id || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, project_manager_id: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a team member</option>
                {teamUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Team Members */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Team Members (Optional)</label>
            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
              {teamUsers.map(user => (
                <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(config.actionConfig?.team_members || []).includes(user.id)}
                    onChange={(e) => {
                      const current = config.actionConfig?.team_members || [];
                      const updated = e.target.checked
                        ? [...current, user.id]
                        : current.filter(id => id !== user.id);
                      setConfig({
                        ...config,
                        actionConfig: { ...config.actionConfig, team_members: updated }
                      });
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">{user.name}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {(config.actionConfig?.team_members || []).length} member(s) selected
            </p>
          </div>

          {/* Create Default Tasks from Template */}
          {config.actionConfig?.template_id && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="create_default_tasks"
                checked={config.actionConfig?.create_default_tasks !== false}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, create_default_tasks: e.target.checked }
                })}
                className="rounded border-gray-300"
              />
              <label htmlFor="create_default_tasks" className="text-sm text-gray-700">
                Create default tasks from template
              </label>
            </div>
          )}

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            Creates a new project under the client from the trigger context. If a template is selected, the project will inherit its stages and default tasks.
          </div>
        </div>
      );
    }

    // Create Client Action - Requires Inbound Webhook Trigger
    if (actionType === 'create_client') {
      // Check if the trigger is an inbound webhook
      const triggerNode = allNodes?.find(n => n.type === 'trigger');
      const isWebhookTrigger = triggerNode?.data?.triggerType === 'inbound_webhook';

      // Helper function to insert variable into client name
      const insertVariableToClientName = (variable) => {
        const currentName = config.actionConfig?.client_name || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, client_name: currentName + variable }
        });
      };

      // Helper function to insert variable into notes
      const insertVariableToClientNotes = (variable) => {
        const currentNotes = config.actionConfig?.notes || '';
        setConfig({
          ...config,
          actionConfig: { ...config.actionConfig, notes: currentNotes + variable }
        });
      };

      if (!isWebhookTrigger) {
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-md">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">Inbound Webhook Required</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    The Create Client action requires an <strong>Inbound Webhook</strong> trigger to receive client data from external systems.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    Please change your trigger to &quot;Inbound Webhook&quot; to use this action.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 p-3 rounded-md text-sm text-green-800">
            <CheckSquare className="w-4 h-4 inline mr-2" />
            Webhook trigger detected. Map your webhook payload fields to client data below.
          </div>

          {/* Client Name - Required */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Client Name <span className="text-red-500">*</span>
              </label>
              <VariablePicker 
                onInsert={insertVariableToClientName} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <input
              type="text"
              value={config.actionConfig?.client_name || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, client_name: e.target.value }
              })}
              placeholder="e.g., {{webhook.company_name}} or {{webhook.name}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Use webhook variables to map incoming data</p>
          </div>

          {/* Client Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Type</label>
            <select
              value={config.actionConfig?.client_type || 'auto'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, client_type: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="auto">Auto-detect</option>
              <option value="business">Business</option>
              <option value="individual">Individual</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Auto-detect: If client name matches contact name, treat as individual; otherwise business
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="text"
              value={config.actionConfig?.email || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, email: e.target.value }
              })}
              placeholder="e.g., {{webhook.email}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <input
              type="text"
              value={config.actionConfig?.phone || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, phone: e.target.value }
              })}
              placeholder="e.g., {{webhook.phone}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            <input
              type="text"
              value={config.actionConfig?.website || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, website: e.target.value }
              })}
              placeholder="e.g., {{webhook.website}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Industry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <input
              type="text"
              value={config.actionConfig?.industry || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, industry: e.target.value }
              })}
              placeholder="e.g., {{webhook.industry}} or Technology"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <input
              type="text"
              value={config.actionConfig?.address || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, address: e.target.value }
              })}
              placeholder="e.g., {{webhook.address}}"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <VariablePicker 
                onInsert={insertVariableToClientNotes} 
                includeAI={false}
                customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                webhookVariables={webhookPayloadVariables}
                portalFields={portalFields}
              />
            </div>
            <textarea
              value={config.actionConfig?.notes || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, notes: e.target.value }
              })}
              placeholder="Additional notes about the client..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Create Primary Contact */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="create_primary_contact"
                checked={config.actionConfig?.create_primary_contact !== false}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, create_primary_contact: e.target.checked }
                })}
                className="rounded border-gray-300"
              />
              <label htmlFor="create_primary_contact" className="text-sm font-medium text-gray-700">
                Create Primary Contact
              </label>
            </div>

            {config.actionConfig?.create_primary_contact !== false && (
              <div className="space-y-3 ml-6 p-3 bg-gray-50 rounded-md">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={config.actionConfig?.contact_name || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      actionConfig: { ...config.actionConfig, contact_name: e.target.value }
                    })}
                    placeholder="e.g., {{webhook.contact_name}}"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
                  <input
                    type="text"
                    value={config.actionConfig?.contact_email || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      actionConfig: { ...config.actionConfig, contact_email: e.target.value }
                    })}
                    placeholder="e.g., {{webhook.contact_email}}"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Role</label>
                  <input
                    type="text"
                    value={config.actionConfig?.contact_role || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      actionConfig: { ...config.actionConfig, contact_role: e.target.value }
                    })}
                    placeholder="e.g., CEO, Manager, {{webhook.role}}"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Create General Project */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="create_general_project"
              checked={config.actionConfig?.create_general_project !== false}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, create_general_project: e.target.checked }
              })}
              className="rounded border-gray-300"
            />
            <label htmlFor="create_general_project" className="text-sm text-gray-700">
              Create &quot;General&quot; project for new client
            </label>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Duplicate Handling:</strong>
            <ul className="mt-1 ml-4 list-disc">
              <li>Same company name + same contact email: Updates existing client</li>
              <li>Same company name + different contact email: Adds contact to existing client</li>
              <li>New company name: Creates new client</li>
            </ul>
            <p className="mt-2">The client/contact IDs will be available for subsequent actions.</p>
          </div>
        </div>
      );
    }

    // Archive Task Action
    if (actionType === 'archive_task') {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archive Action <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.archive_action || 'archive'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, archive_action: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="archive">Archive Task</option>
              <option value="unarchive">Unarchive Task</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Task
            </label>
            <select
              value={config.actionConfig?.target_task || 'trigger_task'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, target_task: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="trigger_task">Task from Trigger (Current Task)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Archives the task that triggered this workflow
            </p>
          </div>

          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>Note:</strong> Archived tasks are hidden from default views but can be restored later. This action is commonly used in workflows triggered by &quot;Task Completed&quot;.
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            {config.actionConfig?.archive_action === 'unarchive' 
              ? 'Restores an archived task, making it visible in task lists again.'
              : 'Moves the task to the archive. The task will be hidden from normal views but can be restored.'}
          </div>
        </div>
      );
    }

    // Send Portal Invite action
    // Grant Portal Access action
    if (actionType === 'grant_portal_access') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            Enable portal access for contact(s) without sending an invitation email
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Which Contacts? <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.contact_selection || 'primary_contact'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, contact_selection: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="primary_contact">Primary Contact Only</option>
              <option value="all_contacts">All Contacts</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {config.actionConfig?.contact_selection === 'primary_contact' && 
                'Grants portal access only to the primary contact of the client'}
              {config.actionConfig?.contact_selection === 'all_contacts' && 
                'Grants portal access to all contacts under the client'}
            </p>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>What this does:</strong>
            <ul className="mt-2 ml-4 list-disc">
              <li>Sets <code className="bg-blue-100 px-1 rounded">portal_enabled = true</code> for the selected contact(s)</li>
              <li>Makes the contact(s) eligible for portal login</li>
              <li>Does NOT send any email - use &quot;Send Portal Invite&quot; action to send emails</li>
            </ul>
          </div>

          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Tip:</strong> Use this action after &quot;Create Client&quot; to enable portal access, then follow with &quot;Send Portal Invite&quot; to email the contact(s).
          </div>
        </div>
      );
    }

    if (actionType === 'send_portal_invite') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            Send a client portal invitation to a contact, enabling them to access the portal
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Source <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.contact_source || 'trigger_contact'}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, contact_source: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="trigger_contact">Contact from Trigger</option>
              <option value="primary_contact">Client&apos;s Primary Contact</option>
              <option value="custom_email">Custom Email Address</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {config.actionConfig?.contact_source === 'trigger_contact' && 
                'Uses the contact associated with the workflow trigger (e.g., from Portal Access Granted trigger)'}
              {config.actionConfig?.contact_source === 'primary_contact' && 
                'Sends invite to the primary contact of the client in context'}
              {config.actionConfig?.contact_source === 'custom_email' && 
                'Specify a custom email address or use a variable'}
            </p>
          </div>

          {config.actionConfig?.contact_source === 'custom_email' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <VariablePicker 
                  onInsert={(value) => {
                    setConfig({
                      ...config,
                      actionConfig: { 
                        ...config.actionConfig, 
                        custom_email: (config.actionConfig?.custom_email || '') + value 
                      }
                    });
                  }}
                  customFields={customFields}
                clientCustomFields={clientCustomFields}
                projectCustomFields={projectCustomFields}
                  webhookVariables={webhookVariables}
                />
              </div>
              <input
                type="text"
                value={config.actionConfig?.custom_email || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, custom_email: e.target.value }
                })}
                placeholder="{{contact.email}} or email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="send_verification_email"
              checked={config.actionConfig?.send_verification_email !== false}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, send_verification_email: e.target.checked }
              })}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="send_verification_email" className="text-sm text-gray-700">
              Send verification email to contact
            </label>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>What this does:</strong>
            <ul className="mt-2 ml-4 list-disc">
              <li>Enables portal access for the contact</li>
              <li>Sends an email with a verification link (if enabled)</li>
              <li>Contact can then set their password and access the portal</li>
            </ul>
          </div>

          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Note:</strong> The contact must exist in the system. Use &quot;Create Client&quot; action first if creating new contacts.
          </div>
        </div>
      );
    }

    // Go To Action - Jump to another node in the workflow
    if (actionType === 'go_to') {
      // Get all nodes in the workflow for selection - use allNodes prop
      const availableNodes = allNodes?.filter(n => n.id !== node?.id && n.type !== 'trigger') || [];
      
      return (
        <div className="space-y-4">
          <div className="bg-purple-50 p-3 rounded-md text-sm text-purple-800">
            <Info className="w-4 h-4 inline mr-2" />
            Jump to another step in this workflow. Can be used for loops or skipping steps.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Go To Node <span className="text-red-500">*</span>
            </label>
            {availableNodes.length === 0 ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                No other nodes available. Add more action or condition nodes to your workflow first.
              </div>
            ) : (
              <select
                value={config.actionConfig?.target_node_id || ''}
                onChange={(e) => setConfig({
                  ...config,
                  actionConfig: { ...config.actionConfig, target_node_id: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a node...</option>
                {availableNodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.data?.label || n.data?.actionType || n.type} ({n.id.substring(0, 8)}...)
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Select the node to jump to. The workflow will continue from that node.
            </p>
          </div>

          <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            <strong>Loop Protection:</strong> If the same node is executed more than 10 times within 60 seconds, the workflow will stop with an error to prevent infinite loops.
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Use cases:</strong>
            <ul className="mt-2 ml-4 list-disc">
              <li>Create loops with If/Else conditions</li>
              <li>Retry actions on failure</li>
              <li>Skip to specific steps based on conditions</li>
            </ul>
          </div>
        </div>
      );
    }

    // Add to Workflow Action - Trigger another workflow
    if (actionType === 'add_to_workflow') {
      return (
        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-md text-sm text-green-800">
            <Info className="w-4 h-4 inline mr-2" />
            Trigger another workflow with the current context (client, contact, task, etc.)
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workflow to Trigger <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.target_workflow_id || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, target_workflow_id: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a workflow...</option>
              {(window.__athena_workflows || []).map(wf => (
                <option key={wf.id} value={wf.id}>
                  {wf.name} {!wf.enabled && '(disabled)'}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              The selected workflow will be triggered immediately with all current context data.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="wait_for_completion"
              checked={config.actionConfig?.wait_for_completion || false}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, wait_for_completion: e.target.checked }
              })}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="wait_for_completion" className="text-sm text-gray-700">
              Wait for the triggered workflow to complete before continuing
            </label>
          </div>

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Context passed:</strong> Client, Contact, Task, Project, and all variables from the current execution.
          </div>
        </div>
      );
    }

    // Remove from Workflow Action - Cancel execution of another workflow or current workflow
    if (actionType === 'remove_from_workflow') {
      // Get available workflows
      const availableWorkflows = window.__athena_workflows || [];
      // Use special value "__current__" for current workflow - backend will resolve it
      const CURRENT_WORKFLOW_VALUE = '__current__';
      
      return (
        <div className="space-y-4">
          <div className="bg-red-50 p-3 rounded-md text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Cancel any running or pending executions of a workflow for the current entity.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Workflow to Cancel <span className="text-red-500">*</span>
            </label>
            <select
              value={config.actionConfig?.target_workflow_id || ''}
              onChange={(e) => setConfig({
                ...config,
                actionConfig: { ...config.actionConfig, target_workflow_id: e.target.value }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select a workflow...</option>
              {/* Current workflow option - always available */}
              <option value={CURRENT_WORKFLOW_VALUE} className="font-semibold">
                ⭐ Current Workflow (This one)
              </option>
              {/* Other workflows */}
              {availableWorkflows.map(wf => (
                <option key={wf.id} value={wf.id}>
                  {wf.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Any running or pending execution for the current entity (client/contact/task) will be cancelled.
            </p>
          </div>

          {config.actionConfig?.target_workflow_id === CURRENT_WORKFLOW_VALUE && (
            <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              <strong>Note:</strong> Removing from the current workflow will stop execution after this action completes. Any subsequent nodes will not run.
            </div>
          )}

          <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-2" />
            <strong>Use cases:</strong>
            <ul className="mt-2 ml-4 list-disc">
              <li>Remove from <strong>current workflow</strong>: Stop processing this workflow for the entity</li>
              <li>Remove from <strong>another workflow</strong>: Cancel onboarding when a client cancels, stop reminders when a task is completed</li>
            </ul>
          </div>
        </div>
      );
    }

    return null;
  };

  // ============ IF/ELSE CONFIGURATION ============
  const renderIfElseConfig = () => {
    // Fall back to node.data.branches if config.branches is not yet loaded
    const branches = config.branches || node?.data?.branches || [{ id: 'branch-1', name: 'Branch 1', conditionGroups: [] }];
    const elseEnabled = config.elseEnabled !== undefined ? config.elseEnabled : (node?.data?.elseEnabled !== false);

    // Condition field options
    const conditionFields = [
      { group: 'Task', fields: [
        { value: 'task.name', label: 'Task Name' },
        { value: 'task.description', label: 'Task Description' },
        { value: 'task.status', label: 'Task Stage' },
        { value: 'task.category', label: 'Task Category' },
        { value: 'task.priority', label: 'Task Priority' },
        { value: 'task.assignee', label: 'Task Assignee' },
        { value: 'task.is_overdue', label: 'Task Is Overdue' },
        { value: 'task.show_in_portal', label: 'Show in Portal' },
        { value: 'task.tags', label: 'Task Tags' },
      ]},
      { group: 'Project', fields: [
        { value: 'project.name', label: 'Project Name' },
        { value: 'project.status', label: 'Project Status' },
        { value: 'project.category', label: 'Project Category' },
        { value: 'project.ownership_type', label: 'Ownership Type (Client/Personal/Company)' },
      ]},
      { group: 'Client', fields: [
        { value: 'client.name', label: 'Client Name' },
        { value: 'client.email', label: 'Client Email' },
      ]},
      { group: 'Portal Submission', fields: [
        { value: 'portal.request_type', label: 'Request Type' },
        { value: 'portal.request_in_scope', label: 'Request In Client Scope' },
        { value: 'portal.matching_scope', label: 'Matching Scope Name' },
        { value: 'portal.priority', label: 'Submitted Priority' },
        { value: 'portal.description', label: 'Request Description' },
        { value: 'portal.reference_tables', label: 'Reference Tables Used' },
        { value: 'portal.reference_table_fields', label: 'Reference Table Field Values' },
      ]},
      { group: 'Webhook', fields: [
        { value: 'webhook_payload', label: 'Webhook Payload Field' },
      ]},
    ];

    // Add custom fields to the options
    const customFieldOptions = customFields.map(f => ({
      value: `custom_field.${f.name}`,
      label: f.label || f.name
    }));
    if (customFieldOptions.length > 0) {
      conditionFields.push({ group: 'Custom Fields', fields: customFieldOptions });
    }

    // Dynamic operator options based on field type
    const getOperatorsForField = (fieldValue) => {
      // Boolean fields
      if (['task.is_overdue', 'task.show_in_portal'].includes(fieldValue)) {
        return [
          { value: 'is_true', label: 'Is True' },
          { value: 'is_false', label: 'Is False' },
        ];
      }
      // Selection fields (category, priority, status)
      if (['task.category', 'task.priority', 'task.status', 'project.status', 'project.category'].includes(fieldValue)) {
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does Not Equal' },
          { value: 'is_empty', label: 'Is Empty' },
          { value: 'is_not_empty', label: 'Is Not Empty' },
        ];
      }
      // Text fields (name, description, email)
      if (['task.name', 'task.description', 'project.name', 'client.name', 'client.email'].includes(fieldValue)) {
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does Not Equal' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does Not Contain' },
          { value: 'starts_with', label: 'Starts With' },
          { value: 'ends_with', label: 'Ends With' },
          { value: 'is_empty', label: 'Is Empty' },
          { value: 'is_not_empty', label: 'Is Not Empty' },
        ];
      }
      // Tags field
      if (fieldValue === 'task.tags') {
        return [
          { value: 'contains', label: 'Has Tag' },
          { value: 'not_contains', label: 'Does Not Have Tag' },
          { value: 'is_empty', label: 'Has No Tags' },
          { value: 'is_not_empty', label: 'Has Tags' },
        ];
      }
      // Assignee field
      if (fieldValue === 'task.assignee') {
        return [
          { value: 'equals', label: 'Is Assigned To' },
          { value: 'not_equals', label: 'Is Not Assigned To' },
          { value: 'is_empty', label: 'Is Unassigned' },
          { value: 'is_not_empty', label: 'Has Assignee' },
        ];
      }
      
      // Custom field type-aware operators
      if (fieldValue && fieldValue.startsWith('custom_field.')) {
        const fieldName = fieldValue.replace('custom_field.', '');
        const fieldDef = customFieldDefinitions.find(f => f.name === fieldName);
        
        if (fieldDef) {
          const fieldType = fieldDef.type || fieldDef.field_type || 'text';
          
          switch (fieldType) {
            case 'number':
            case 'currency':
              return [
                { value: 'equals', label: 'Equals (=)' },
                { value: 'not_equals', label: 'Does Not Equal (≠)' },
                { value: 'greater_than', label: 'Greater Than (>)' },
                { value: 'greater_than_or_equal', label: 'Greater Than or Equal (≥)' },
                { value: 'less_than', label: 'Less Than (<)' },
                { value: 'less_than_or_equal', label: 'Less Than or Equal (≤)' },
                { value: 'between', label: 'Between (Range)' },
                { value: 'not_between', label: 'Not Between' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Has Value' },
              ];
            case 'boolean':
            case 'checkbox':
              return [
                { value: 'is_true', label: 'Is True / Yes' },
                { value: 'is_false', label: 'Is False / No' },
              ];
            case 'dropdown':
            case 'select':
            case 'radio':
              return [
                { value: 'equals', label: 'Equals' },
                { value: 'not_equals', label: 'Does Not Equal' },
                { value: 'in_list', label: 'Is One Of' },
                { value: 'not_in_list', label: 'Is Not One Of' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Has Value' },
              ];
            case 'date':
            case 'datetime':
              return [
                { value: 'equals', label: 'Is On' },
                { value: 'not_equals', label: 'Is Not On' },
                { value: 'before', label: 'Is Before' },
                { value: 'after', label: 'Is After' },
                { value: 'between', label: 'Is Between' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Has Date' },
              ];
            case 'text':
            case 'textarea':
            case 'email':
            case 'url':
            case 'phone':
            default:
              return [
                { value: 'equals', label: 'Equals' },
                { value: 'not_equals', label: 'Does Not Equal' },
                { value: 'contains', label: 'Contains' },
                { value: 'not_contains', label: 'Does Not Contain' },
                { value: 'starts_with', label: 'Starts With' },
                { value: 'ends_with', label: 'Ends With' },
                { value: 'is_empty', label: 'Is Empty' },
                { value: 'is_not_empty', label: 'Has Value' },
              ];
          }
        }
      }
      
      // Default operators for other fields
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Does Not Equal' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does Not Contain' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'is_empty', label: 'Is Empty' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'is_true', label: 'Is True' },
        { value: 'is_false', label: 'Is False' },
      ];
    };

    // Priority options
    const priorityOptions = [
      { value: 'low', label: 'Low' },
      { value: 'normal', label: 'Normal' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
    ];

    // Project status options
    const projectStatusOptions = [
      { value: 'open', label: 'Open' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' },
    ];

    // Ownership type options (Client/Personal/Company)
    const ownershipTypeOptions = [
      { value: 'client', label: 'Client' },
      { value: 'personal', label: 'Personal' },
      { value: 'company', label: organizationName },
    ];

    // Get value input type for a field
    const getValueInputType = (fieldValue) => {
      if (['task.category'].includes(fieldValue)) return 'task_category';
      if (['task.priority'].includes(fieldValue)) return 'priority';
      if (['task.status', 'project.status'].includes(fieldValue)) return 'status';
      if (['project.category'].includes(fieldValue)) return 'project_category';
      if (['project.ownership_type'].includes(fieldValue)) return 'ownership_type';
      if (['task.assignee'].includes(fieldValue)) return 'assignee';
      if (['task.is_overdue', 'task.show_in_portal', 'portal.request_in_scope'].includes(fieldValue)) return 'boolean';
      if (['portal.request_type'].includes(fieldValue)) return 'request_type_select';
      
      // Custom field type detection
      if (fieldValue && fieldValue.startsWith('custom_field.')) {
        const fieldName = fieldValue.replace('custom_field.', '');
        const fieldDef = customFieldDefinitions.find(f => f.name === fieldName);
        
        if (fieldDef) {
          const fieldType = fieldDef.type || fieldDef.field_type || 'text';
          switch (fieldType) {
            case 'number':
            case 'currency':
              return 'number';
            case 'boolean':
            case 'checkbox':
              return 'boolean';
            case 'dropdown':
            case 'select':
            case 'radio':
              return 'custom_dropdown';
            case 'date':
            case 'datetime':
              return 'date';
            default:
              return 'text';
          }
        }
      }
      
      return 'text';
    };
    
    // Helper to get custom field definition
    const getCustomFieldDef = (fieldValue) => {
      if (!fieldValue || !fieldValue.startsWith('custom_field.')) return null;
      const fieldName = fieldValue.replace('custom_field.', '');
      return customFieldDefinitions.find(f => f.name === fieldName);
    };

    
// Render request type dropdown for conditions
const renderRequestTypeSelector = () => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Request Type
      </label>
      <select
        value={config.conditionConfig?.request_type || ''}
        onChange={(e) => setConfig({
          ...config,
          conditionConfig: { ...config.conditionConfig, request_type: e.target.value }
        })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
      >
        <option value="">Select Request Type</option>
        {portalFields.map((type, index) => (
          <option key={index} value={type.value}>{type.label}</option>
        ))}
      </select>
    </div>
  );
};

// Render dynamic value input based on field type
    const renderValueInput = (condition, branchId, groupIndex, condIndex) => {
      const inputType = getValueInputType(condition.field);
      const operator = condition.operator;
      
      // Hide value input for these operators
      if (['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(operator)) {
        return null;
      }
      
      // Range input for 'between' and 'not_between' operators
      if (['between', 'not_between'].includes(operator)) {
        return (
          <div className="flex items-center gap-2 flex-1">
            <input
              type="number"
              placeholder="Min"
              value={condition.value_min || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value_min: e.target.value })}
              className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
            <span className="text-gray-500 text-sm">and</span>
            <input
              type="number"
              placeholder="Max"
              value={condition.value_max || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value_max: e.target.value })}
              className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
          </div>
        );
      }
      
      // Multi-select for 'in_list' and 'not_in_list' operators with dropdown custom fields
      if (['in_list', 'not_in_list'].includes(operator)) {
        const fieldDef = getCustomFieldDef(condition.field);
        if (fieldDef?.options?.length > 0) {
          return (
            <div className="flex-1 space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 bg-white">
              {fieldDef.options.map(option => (
                <label key={option} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={(condition.value_list || []).includes(option)}
                    onChange={(e) => {
                      const current = condition.value_list || [];
                      const updated = e.target.checked
                        ? [...current, option]
                        : current.filter(v => v !== option);
                      updateCondition(branchId, groupIndex, condIndex, { value_list: updated });
                    }}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          );
        }
        // Fallback to text input for value_list
        return (
          <input
            type="text"
            value={(condition.value_list || []).join(', ')}
            onChange={(e) => {
              const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
              updateCondition(branchId, groupIndex, condIndex, { value_list: values });
            }}
            placeholder="Value1, Value2, ..."
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      switch (inputType) {
        case 'task_category':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select category...</option>
              {taskCategories.map(cat => (
                <option key={cat.id || cat} value={cat.name || cat}>{cat.name || cat}</option>
              ))}
            </select>
          );
        case 'project_category':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select category...</option>
              {projectCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          );
        case 'priority':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select priority...</option>
              {priorityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'status':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select status...</option>
              {projectStatusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'request_type_select':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select request type...</option>
              {requestTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'ownership_type':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select ownership type...</option>
              {ownershipTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'assignee':
          return (
            <select
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            >
              <option value="">Select team member...</option>
              {teamUsers.map(user => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </select>
          );
        case 'boolean':
          return null; // Boolean fields use is_true/is_false operators
        case 'number':
          return (
            <input
              type="number"
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              placeholder="Enter number..."
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
          );
        case 'date':
          return (
            <input
              type="date"
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
          );
        case 'custom_dropdown':
          const fieldDef = getCustomFieldDef(condition.field);
          if (fieldDef?.options?.length > 0) {
            return (
              <select
                value={condition.value || ''}
                onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
              >
                <option value="">Select value...</option>
                {fieldDef.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            );
          }
          // Fallback to text input if no options
          return (
            <input
              type="text"
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              placeholder="Value..."
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
          );
        default:
          return (
            <input
              type="text"
              value={condition.value || ''}
              onChange={(e) => updateCondition(branchId, groupIndex, condIndex, { value: e.target.value })}
              placeholder="Value..."
              className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
            />
          );
      }
    };

    // Helper to update branches - use functional update to avoid stale closure
    const updateBranches = (newBranches) => {
      setConfig(prevConfig => ({ ...prevConfig, branches: newBranches }));
    };

    // Add a new branch
    const addBranch = () => {
      if (branches.length >= 10) {
        alert('Maximum 10 branches allowed');
        return;
      }
      const newBranch = {
        id: `branch-${Date.now()}`,
        name: `Branch ${branches.length + 1}`,
        conditionGroups: [{ operator: 'AND', conditions: [] }],
        groupOperator: 'OR'
      };
      updateBranches([...branches, newBranch]);
    };

    // Remove a branch
    const removeBranch = (branchId) => {
      if (branches.length <= 1) {
        alert('At least one branch is required');
        return;
      }
      updateBranches(branches.filter(b => b.id !== branchId));
    };

    // Update a specific branch
    const updateBranch = (branchId, updates) => {
      updateBranches(branches.map(b => b.id === branchId ? { ...b, ...updates } : b));
    };

    // Add condition group (OR group)
    const addConditionGroup = (branchId) => {
      const branch = branches.find(b => b.id === branchId);
      const newGroups = [...(branch.conditionGroups || []), { operator: 'AND', conditions: [] }];
      updateBranch(branchId, { conditionGroups: newGroups });
    };

    // Remove condition group
    const removeConditionGroup = (branchId, groupIndex) => {
      const branch = branches.find(b => b.id === branchId);
      const newGroups = branch.conditionGroups.filter((_, i) => i !== groupIndex);
      updateBranch(branchId, { conditionGroups: newGroups.length > 0 ? newGroups : [{ operator: 'AND', conditions: [] }] });
    };

    // Add condition to a group
    const addCondition = (branchId, groupIndex) => {
      const branch = branches.find(b => b.id === branchId);
      const newGroups = [...branch.conditionGroups];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        conditions: [...(newGroups[groupIndex].conditions || []), { field: '', operator: 'equals', value: '' }]
      };
      updateBranch(branchId, { conditionGroups: newGroups });
    };

    // Remove condition from a group
    const removeCondition = (branchId, groupIndex, conditionIndex) => {
      const branch = branches.find(b => b.id === branchId);
      const newGroups = [...branch.conditionGroups];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        conditions: newGroups[groupIndex].conditions.filter((_, i) => i !== conditionIndex)
      };
      updateBranch(branchId, { conditionGroups: newGroups });
    };

    // Update a specific condition
    const updateCondition = (branchId, groupIndex, conditionIndex, updates) => {
      const branch = branches.find(b => b.id === branchId);
      const newGroups = [...branch.conditionGroups];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        conditions: newGroups[groupIndex].conditions.map((c, i) => 
          i === conditionIndex ? { ...c, ...updates } : c
        )
      };
      updateBranch(branchId, { conditionGroups: newGroups });
    };

    // Get color for branch index
    const getBranchColor = (index) => {
      const colors = ['green', 'blue', 'amber', 'red', 'violet', 'pink', 'teal', 'orange', 'indigo', 'lime'];
      return colors[index % colors.length];
    };

    return (
      <div className="space-y-6">
        {/* Info box */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-start gap-2">
            <GitBranch className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-purple-900">Multi-Branch Conditional Logic</h4>
              <p className="text-sm text-purple-700 mt-1">
                Create up to 10 branches with complex AND/OR conditions. 
                Branches are evaluated in order - the first matching branch is executed.
              </p>
            </div>
          </div>
        </div>

        {/* Conflict Warnings */}
        {(() => {
          const warnings = detectBranchConflicts(branches);
          if (warnings.length === 0) return null;
          
          return (
            <div className="space-y-2">
              {warnings.map((warning, index) => {
                const colorClasses = getWarningSeverityClass(warning.severity);
                return (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg flex items-start gap-2 ${colorClasses.bg} border ${colorClasses.border}`}
                  >
                    <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colorClasses.icon}`} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${colorClasses.text}`}>
                        {warning.message}
                      </p>
                      {warning.suggestion && (
                        <p className="text-xs text-gray-600 mt-1">
                          💡 {warning.suggestion}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Branches */}
        <div className="space-y-4">
          {branches.map((branch, branchIndex) => (
            <div 
              key={branch.id} 
              className={`border-2 rounded-lg overflow-hidden border-${getBranchColor(branchIndex)}-300`}
            >
              {/* Branch Header */}
              <div className={`bg-${getBranchColor(branchIndex)}-50 px-4 py-3 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-${getBranchColor(branchIndex)}-500`}></div>
                  <input
                    type="text"
                    value={branch.name}
                    onChange={(e) => updateBranch(branch.id, { name: e.target.value })}
                    className="font-medium bg-transparent border-none focus:ring-0 p-0 text-gray-900"
                    placeholder="Branch name..."
                  />
                </div>
                <button
                  onClick={() => removeBranch(branch.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove branch"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Condition Groups */}
              <div className="p-4 space-y-3">
                {(branch.conditionGroups || []).map((group, groupIndex) => (
                  <div key={groupIndex}>
                    {/* OR separator between groups */}
                    {groupIndex > 0 && (
                      <div className="flex items-center gap-2 my-3">
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">OR</span>
                        <div className="flex-1 h-px bg-gray-300"></div>
                      </div>
                    )}

                    {/* Condition Group Box */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500">
                          Condition Group {groupIndex + 1} (All must match - AND)
                        </span>
                        {branch.conditionGroups.length > 1 && (
                          <button
                            onClick={() => removeConditionGroup(branch.id, groupIndex)}
                            className="text-gray-400 hover:text-red-500 text-xs"
                          >
                            Remove Group
                          </button>
                        )}
                      </div>

                      {/* Conditions in this group */}
                      <div className="space-y-3">
                        {(group.conditions || []).map((condition, condIndex) => (
                          <div key={condIndex} className="flex flex-wrap items-center gap-2">
                            {condIndex > 0 && (
                              <span className="text-xs font-bold text-blue-600 w-10 shrink-0">AND</span>
                            )}
                            {condIndex === 0 && <span className="text-xs text-gray-400 w-10 shrink-0">If</span>}
                            
                            {/* Field Select */}
                            <select
                              value={condition.field}
                              onChange={(e) => {
                                // When field changes, reset operator to first valid option and clear value
                                const newField = e.target.value;
                                const validOperators = getOperatorsForField(newField);
                                const newOperator = validOperators[0]?.value || 'equals';
                                updateCondition(branch.id, groupIndex, condIndex, { 
                                  field: newField, 
                                  operator: newOperator,
                                  value: '' 
                                });
                              }}
                              className="min-w-[140px] flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500"
                            >
                              <option value="">Select field...</option>
                              {conditionFields.map(group => (
                                <optgroup key={group.group} label={group.group}>
                                  {group.fields.map(field => (
                                    <option key={field.value} value={field.value}>{field.label}</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>

                            {/* Operator Select - Dynamic based on field */}
                            <select
                              value={condition.operator}
                              onChange={(e) => updateCondition(branch.id, groupIndex, condIndex, { operator: e.target.value })}
                              className="min-w-[120px] w-auto px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-purple-500 shrink-0"
                            >
                              {getOperatorsForField(condition.field).map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>

                            {/* Dynamic Value Input based on field type */}
                            <div className="min-w-[150px] flex-1">
                              {renderValueInput(condition, branch.id, groupIndex, condIndex)}
                            </div>

                            {/* Remove Condition */}
                            <button
                              onClick={() => removeCondition(branch.id, groupIndex, condIndex)}
                              className="text-gray-400 hover:text-red-500 shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}

                        {/* Add Condition Button */}
                        <button
                          onClick={() => addCondition(branch.id, groupIndex)}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                        >
                          + Add Condition
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add OR Group Button */}
                <button
                  onClick={() => addConditionGroup(branch.id)}
                  className="w-full py-2 text-sm text-orange-600 hover:text-orange-800 font-medium border border-dashed border-orange-300 rounded-lg hover:bg-orange-50 transition-colors"
                >
                  + Add OR Condition Group
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add Branch Button */}
        {branches.length < 10 && (
          <button
            onClick={addBranch}
            className="w-full py-3 text-sm font-medium text-purple-600 hover:text-purple-800 border-2 border-dashed border-purple-300 rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
          >
            <GitBranch className="w-4 h-4" />
            Add Branch ({branches.length}/10)
          </button>
        )}

        {/* Else Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
          <div>
            <h4 className="font-medium text-gray-900">Else Branch (Default)</h4>
            <p className="text-sm text-gray-600">Execute when no other branch matches</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={elseEnabled}
              onChange={(e) => setConfig(prevConfig => ({ ...prevConfig, elseEnabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">Branch Summary</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            {branches.map((branch, i) => {
              const condCount = (branch.conditionGroups || []).reduce(
                (sum, g) => sum + (g.conditions?.length || 0), 0
              );
              const groupCount = (branch.conditionGroups || []).length;
              return (
                <li key={branch.id} className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full bg-${getBranchColor(i)}-500`}></div>
                  <strong>{branch.name}:</strong> 
                  {condCount} condition{condCount !== 1 ? 's' : ''} in {groupCount} group{groupCount !== 1 ? 's' : ''}
                </li>
              );
            })}
            {elseEnabled && (
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                <strong>Else:</strong> Runs if no branches match
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  const getModalTitle = () => {
    if (node?.type === 'trigger') return 'Configure Trigger';
    if (node?.type === 'condition') return 'Configure Condition';
    if (node?.type === 'ifelse') return 'Configure If/Else Branches';
    if (node?.type === 'action') return 'Configure Action';
    return 'Configure Node';
  };

  const getModalIcon = () => {
    if (node?.type === 'trigger') return <Zap className="w-5 h-5 text-green-600" />;
    if (node?.type === 'condition') return <GitBranch className="w-5 h-5 text-yellow-600" />;
    if (node?.type === 'ifelse') return <GitBranch className="w-5 h-5 text-purple-600" />;
    if (node?.type === 'action') {
      const actionType = config.actionType || node?.data?.actionType;
      if (actionType === 'webhook') return <Send className="w-5 h-5 text-blue-600" />;
      if (actionType === 'email') return <Mail className="w-5 h-5 text-blue-600" />;
      if (actionType === 'send_portal_invite') return <Mail className="w-5 h-5 text-green-600" />;
      if (actionType === 'grant_portal_access') return <Users className="w-5 h-5 text-green-600" />;
      if (actionType === 'ai_generate') return <Sparkles className="w-5 h-5 text-purple-600" />;
      if (actionType === 'update_project_manager') return <Users className="w-5 h-5 text-blue-600" />;
      if (actionType === 'create_task') return <CheckSquare className="w-5 h-5 text-blue-600" />;
      if (actionType === 'create_project') return <CheckSquare className="w-5 h-5 text-green-600" />;
      if (actionType === 'create_client') return <Users className="w-5 h-5 text-green-600" />;
      if (actionType === 'archive_task') return <Target className="w-5 h-5 text-amber-600" />;
    }
    return <Zap className="w-5 h-5 text-gray-600" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[90vw] md:max-w-2xl lg:max-w-3xl xl:max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getModalIcon()}
            <h2 className="text-xl font-bold text-gray-900">{getModalTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Node Label
            </label>
            <input
              type="text"
              value={config.label || node?.data?.label || ''}
              onChange={(e) => setConfig({ ...config, label: e.target.value })}
              placeholder="Enter a descriptive label..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {node?.type === 'trigger' && renderTriggerConfig()}
          {node?.type === 'condition' && renderConditionConfig()}
          {node?.type === 'ifelse' && renderIfElseConfig()}
          {node?.type === 'action' && renderActionConfig()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            {applyStatus === 'saved' ? (
              <>
                <CheckSquare className="w-4 h-4" />
                Saved
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigModal;


